import { gqlGetProductAggregationsBySku, gqlGetProductsBySlug, gqlGetProductsBySku, AmerOrderHistoryProvider } from '@arcteryx/data-providers';
import striptags from 'striptags';
import { getDistance } from 'geolib';
import { getCurrencyCode as getCurrencyCode$1 } from '@arcteryx/js-price';
import { getUserProfile, setForgeRockUser, setSessionStorage, getForgeRockUser, saveUserProfile, changeUserPassword, logout } from '@arcteryx/js-auth';

function ownKeys(object, enumerableOnly) {
  var keys = Object.keys(object);
  if (Object.getOwnPropertySymbols) {
    var symbols = Object.getOwnPropertySymbols(object);
    enumerableOnly && (symbols = symbols.filter(function (sym) {
      return Object.getOwnPropertyDescriptor(object, sym).enumerable;
    })), keys.push.apply(keys, symbols);
  }
  return keys;
}
function _objectSpread2(target) {
  for (var i = 1; i < arguments.length; i++) {
    var source = null != arguments[i] ? arguments[i] : {};
    i % 2 ? ownKeys(Object(source), !0).forEach(function (key) {
      _defineProperty(target, key, source[key]);
    }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(target, Object.getOwnPropertyDescriptors(source)) : ownKeys(Object(source)).forEach(function (key) {
      Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key));
    });
  }
  return target;
}
function _defineProperty(obj, key, value) {
  if (key in obj) {
    Object.defineProperty(obj, key, {
      value: value,
      enumerable: true,
      configurable: true,
      writable: true
    });
  } else {
    obj[key] = value;
  }
  return obj;
}
function _objectWithoutPropertiesLoose(source, excluded) {
  if (source == null) return {};
  var target = {};
  var sourceKeys = Object.keys(source);
  var key, i;
  for (i = 0; i < sourceKeys.length; i++) {
    key = sourceKeys[i];
    if (excluded.indexOf(key) >= 0) continue;
    target[key] = source[key];
  }
  return target;
}
function _objectWithoutProperties(source, excluded) {
  if (source == null) return {};
  var target = _objectWithoutPropertiesLoose(source, excluded);
  var key, i;
  if (Object.getOwnPropertySymbols) {
    var sourceSymbolKeys = Object.getOwnPropertySymbols(source);
    for (i = 0; i < sourceSymbolKeys.length; i++) {
      key = sourceSymbolKeys[i];
      if (excluded.indexOf(key) >= 0) continue;
      if (!Object.prototype.propertyIsEnumerable.call(source, key)) continue;
      target[key] = source[key];
    }
  }
  return target;
}

/*eslint-disable */

// add some flexibility later so we could potentially coerce these into a defined type (boolean | number | etc)
class Attr {
  constructor(value) {
    this.value = this.make(value);
  }
  coerce(value) {
    return value;
  }

  // return the coerced value if given, otherwise the default value
  make(value) {
    return value !== undefined ? this.coerce(value) : this.value;
  }
  describe() {
    return "<any>";
  }
}
class List extends Attr {
  describe() {
    return ["<any>"];
  }
}
class Number extends Attr {
  coerce(value) {
    if (typeof value === "string") {
      value = parseFloat(value);
    } else if (typeof value === "boolean") {
      value = value ? 1 : 0;
    }
    return value;
  }
  describe() {
    return "<number>";
  }
}
class String$1 extends Attr {
  coerce(value) {
    return value == undefined || typeof value === "string" ? value : `${value}`;
  }
  describe() {
    return "<string>";
  }
}
class Html extends String$1 {
  describe() {
    return "<html>";
  }
}
class Boolean$1 extends Attr {
  coerce(value) {
    if (typeof value === "number") {
      value = !!value;
    } else if (typeof value === "string") {
      if (value.length === 0) {
        value = false;
      } else {
        const int = parseInt(value, 0);
        value = isNaN(int) ? true : !!int;
      }
    }
    return value;
  }
  describe() {
    return "<boolean>";
  }
}
class RelateOne extends Attr {
  constructor(ModelClass, value) {
    super();
    this.ModelClass = ModelClass;
    this.value = this.make(value);
  }
  make(value) {
    return value !== undefined ? new this.ModelClass(value) : this.value;
  }
  describe() {
    return _objectSpread2({}, this.ModelClass.$schema());
  }
}
class RelateMany extends RelateOne {
  make(value) {
    return value !== undefined && Array.isArray(value) ? value.map(item => new this.ModelClass(item)) : this.value;
  }
  describe() {
    return [super.describe()];
  }
}
var Attributes = {
  Attr,
  List,
  Number,
  String: String$1,
  Html,
  Boolean: Boolean$1,
  RelateOne,
  RelateMany
};
/*eslint-enable */

class Model {
  constructor(record) {
    this.fill(record);
  }
  static $fields() {
    return {};
  }
  static $attr(defaultValue) {
    return new Attributes.Attr(defaultValue);
  }

  // generic $attr but named such for self-documentation
  static $array(defaultValue) {
    return new Attributes.List(defaultValue);
  }
  static $boolean(defaultValue) {
    return new Attributes.Boolean(defaultValue);
  }
  static $string(defaultValue) {
    return new Attributes.String(defaultValue);
  }

  // Just a string for now, but can possibly do more with this in the future
  // Allows models to explicitly declare that the attribute contains HTML
  static $html(defaultValue) {
    return new Attributes.Html(defaultValue);
  }
  static $number(defaultValue) {
    return new Attributes.Number(defaultValue);
  }
  static $relateOne(ModelClass, defaultValue) {
    return new Attributes.RelateOne(ModelClass, defaultValue);
  }
  static $relateMany(ModelClass, defaultValue) {
    return new Attributes.RelateMany(ModelClass, defaultValue);
  }
  static $extractFields() {
    let fieldsWeWant = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : [];
    const fieldsWeHave = this.$fields();
    return fieldsWeWant.reduce((cur, key) => {
      cur[key] = fieldsWeHave[key];
      return cur;
    }, {});
  }
  static $schema() {
    const fields = this.$fields();
    const schema = {};
    Object.keys(fields).forEach(key => {
      schema[key] = fields[key].describe();
    });
    return schema;
  }
  fill(record) {
    const fields = this.self().$fields();
    for (const key in fields) {
      const field = fields[key];
      const value = record ? record[key] : undefined;
      this[key] = field.make(value);
    }
  }
  setAttribute(key, value) {
    const fields = this.self().$fields();
    const field = fields[key];
    if (field) {
      this[key] = field.make(value);
    }
  }

  // can we maybe just use JSON.parse(JSON.stringify(this)) ? This would include extra junk that user might have added however:
  // let obj = new Model(...);  obj.junk = 'blah';
  //
  // Consider recursion if we use relationships like hasOne, hasMany where this model contains objects of other models
  pojo() {
    const record = {};
    const fields = this.self().$fields();

    // don't include undefined values
    for (const key in fields) {
      // const f = fields[key]; // todo jamie: check why I had this here
      const v = this[key];
      if (v !== undefined) {
        record[key] = v;
      }
    }
    return record;
  }
  self() {
    return this.constructor;
  }
}

class Image extends Model {
  static $fields() {
    return {
      // id: this.$attr(),    // english always label ? (for analytics)
      label: this.$string(),
      // translated
      pathname: this.$string(),
      url: this.$string(),
      colourLabel: this.$string()
    };
  }
}

class VariantOptions extends Model {
  static $fields() {
    return {
      id: this.$attr(),
      code: this.$string(),
      label: this.$string(),
      selected: this.$attr(),
      options: this.$attr()
    };
  }
  get selectedOption() {
    var _this$options;
    return (_this$options = this.options) === null || _this$options === void 0 ? void 0 : _this$options.find(option => option.value === this.selected);
  }
}
class ColourOption extends Model {
  static $fields() {
    return {
      badges: this.$array(),
      label: this.$string(),
      value: this.$attr(),
      image: this.$relateOne(Image),
      heroImage: this.$relateOne(Image),
      thumbnail: this.$relateOne(Image),
      hexCode: this.$string(),
      hexValueSecondary: this.$string(),
      primaryColour: this.$string(),
      isDark: this.$boolean()
    };
  }
}
class SizeOption extends Model {
  static $fields() {
    return {
      label: this.$string(),
      value: this.$attr()
    };
  }
}
class ColourVariantOptions extends VariantOptions {
  static $fields() {
    return _objectSpread2(_objectSpread2({}, super.$fields()), {}, {
      options: this.$relateMany(ColourOption)
    });
  }
}
class SizeVariantOptions extends VariantOptions {
  static $fields() {
    return _objectSpread2(_objectSpread2({}, super.$fields()), {}, {
      options: this.$relateMany(SizeOption)
    });
  }
}

class Badge extends Model {
  static $fields() {
    return {
      code: this.$string(),
      label: this.$string()
    };
  }
}

class Variant extends Model {
  static $fields() {
    return {
      availableIn: this.$number(),
      // days until available again
      badges: this.$relateMany(Badge),
      colourId: this.$attr(),
      discountPrice: this.$number(),
      id: this.$attr(),
      // aka: SKU
      inventory: this.$number(),
      price: this.$number(),
      sizeId: this.$attr(),
      upc: this.$attr()
    };
  }
}

// minimum attributes to support:
// PDP, Product Tile (PLP), Cart, DataLayer (Analytics)

class Product extends Model {
  constructor(record) {
    super(record);
    this.urlReducers = [];
  }
  static $fields() {
    return {
      id: this.$attr(),
      // aka Product Model Number
      slug: this.$string(),
      // mens/alpha-sv-jacket or chunky-knit-toque
      name: this.$attr(),
      // Atom LT Hoody or Veste Alpha SV (translated)
      analyticsName: this.$string(),
      // Atom LT Hoody Men's (always english)
      market: this.$string(),
      currencyCode: this.$string(),
      price: this.$number(),
      discountPrice: this.$number(),
      // expect null if no discount

      gender: this.$string(),
      // men or women or unisex
      collection: this.$string(),
      // veilance | system_a | pro
      collectionLabel: this.$string(),
      // Veilance | System A | Pro

      colourOptions: this.$relateOne(ColourVariantOptions),
      sizeOptions: this.$relateOne(SizeVariantOptions),
      variants: this.$relateMany(Variant),
      isReturnable: this.$boolean(),
      sizeScheme: this.$string(),
      // For footwear sizing eg: us, uk, eur, mondo
      isFootwear: this.$boolean(false),
      isNewSizing: this.$boolean()
    };
  }

  /**
   * @return the absolute savings. For example: price: 100.50, discountPrice: 80 - return 20.5
   */
  get savings() {
    return this.discountPrice ? this.price - this.discountPrice : 0;
  }

  /**
   * @return The percentage savings rounded to 1 decimal place. Examples:
   *   price: 100, discountPrice: 75 - return 25
   *   price: 100, discountPrice: 37.50 - return 62.5
   */
  get savingsPercentage() {
    return this.discountPrice ? Math.round(this.savings / this.price * 1000) / 10 : 0;
  }
  get selectedVariant() {
    var _this$colourOptions, _this$colourOptions$o, _this$sizeOptions, _this$sizeOptions$opt, _this$variants;
    const expectColour = ((_this$colourOptions = this.colourOptions) === null || _this$colourOptions === void 0 ? void 0 : (_this$colourOptions$o = _this$colourOptions.options) === null || _this$colourOptions$o === void 0 ? void 0 : _this$colourOptions$o.length) > 0;
    const expectSize = ((_this$sizeOptions = this.sizeOptions) === null || _this$sizeOptions === void 0 ? void 0 : (_this$sizeOptions$opt = _this$sizeOptions.options) === null || _this$sizeOptions$opt === void 0 ? void 0 : _this$sizeOptions$opt.length) > 0;
    const colour = expectColour ? this.colourOptions.selected : null;
    const size = expectSize ? this.sizeOptions.selected : null;
    return (_this$variants = this.variants) === null || _this$variants === void 0 ? void 0 : _this$variants.find(variant => (!expectColour || variant.colourId === colour) && (!expectSize || variant.sizeId === size));
  }
  get selectedColour() {
    var _this$colourOptions2;
    return (_this$colourOptions2 = this.colourOptions) === null || _this$colourOptions2 === void 0 ? void 0 : _this$colourOptions2.selectedOption;
  }

  /**
   * @return An array of badges for the selected colour
   */
  get selectedColourBadges() {
    var _this$variants2;
    const colour = this.selectedColour;
    const variant = (_this$variants2 = this.variants) === null || _this$variants2 === void 0 ? void 0 : _this$variants2.find(v => v.colourId === (colour === null || colour === void 0 ? void 0 : colour.value));
    return (variant === null || variant === void 0 ? void 0 : variant.badges) || [];
  }
  get selectedSize() {
    var _this$sizeOptions2;
    return (_this$sizeOptions2 = this.sizeOptions) === null || _this$sizeOptions2 === void 0 ? void 0 : _this$sizeOptions2.selectedOption;
  }
  get colourId() {
    var _this$colourOptions3;
    return (_this$colourOptions3 = this.colourOptions) === null || _this$colourOptions3 === void 0 ? void 0 : _this$colourOptions3.selected;
  }
  set colourId(id) {
    this.colourOptions.selected = id;
  }
  get sizeId() {
    var _this$sizeOptions3;
    return (_this$sizeOptions3 = this.sizeOptions) === null || _this$sizeOptions3 === void 0 ? void 0 : _this$sizeOptions3.selected;
  }
  set sizeId(id) {
    this.sizeOptions.selected = id;
  }
  get sku() {
    const variant = this.selectedVariant;
    return variant === null || variant === void 0 ? void 0 : variant.id;
  }

  // Finds the correct variant based on the given id.
  // With the variant, set the selectedColour and selectedSize if they're available
  set sku(id) {
    const variant = this.variants.find(v => v.id === id);
    if (variant) {
      var _this$colourOptions4, _this$colourOptions4$, _this$sizeOptions4, _this$sizeOptions4$op;
      if (variant.colourId && (_this$colourOptions4 = this.colourOptions) !== null && _this$colourOptions4 !== void 0 && (_this$colourOptions4$ = _this$colourOptions4.options) !== null && _this$colourOptions4$ !== void 0 && _this$colourOptions4$.length && this.colourOptions.options.find(opt => opt.value === variant.colourId)) {
        this.colourOptions.selected = variant.colourId;
      }
      if (variant.sizeId && (_this$sizeOptions4 = this.sizeOptions) !== null && _this$sizeOptions4 !== void 0 && (_this$sizeOptions4$op = _this$sizeOptions4.options) !== null && _this$sizeOptions4$op !== void 0 && _this$sizeOptions4$op.length && this.sizeOptions.options.find(opt => opt.value === variant.sizeId)) {
        this.sizeOptions.selected = variant.sizeId;
      }
    } else {
      // unset the options
      if (this.colourOptions) {
        delete this.colourOptions.selected;
      }
      if (this.sizeOptions) {
        delete this.sizeOptions.selected;
      }
    }
  }

  /**
   * Add a function that transforms this.url into some other url
   * @param func A function that takes a `url` and returns a different `url`.
   *   The `this` value of `func` will be this Product instance.
   *   Example: addUrlReducer(url => `/ca/en${url}`) // this.url === `/ca/en/shop/${this.slug}`
   */
  addUrlReducer(func) {
    this.urlReducers.push(func);
    return this;
  }
  get url() {
    return this.urlReducers.reduce((currentUrl, reducer) => reducer.call(this, currentUrl), `/shop/${this.slug}`);
  }

  /**
   * Update the `discountPrice` attribute of the given array of products with the given personalizedPriceProvider object
   * @param products[] An array of Products
   * @param personalizedPriceProvider An instantiated object of data-provider::PersonalizedPriceProvider
   */
  static async $updatePersonalizedPrices(products, personalizedPriceProvider) {
    await personalizedPriceProvider.fetchPrices(products.map(product => product.id));
    products.forEach(product => {
      const discountPrice = personalizedPriceProvider.personalizePrice(product.id, product.price, product.isPro);
      if (discountPrice !== null && discountPrice < product.price && discountPrice >= 0) {
        product.discountPrice = discountPrice;
      }
    });
  }
  static async $updateRealtimePrices(products, personalizedPriceProvider) {
    await personalizedPriceProvider.fetchPrices(products.map(product => product.id));
    products.forEach(product => {
      const realtimePrices = personalizedPriceProvider.getRealtimePrices(product.id);
      if (realtimePrices) {
        const {
          price,
          discountPrice,
          minPrice,
          minDiscountPrice
        } = realtimePrices;
        if (discountPrice !== null && discountPrice < product.price && discountPrice >= 0) {
          product.discountPrice = discountPrice;
        }
        if (minDiscountPrice !== null && minDiscountPrice >= 0) {
          product.minDiscountPrice = minDiscountPrice;
        }
        product.price = price;
        product.minPrice = minPrice;
      }
    });
  }

  /**
   * Update the `variants[].inventory attributes of the given array of products with the given inventoryProvider object
   * @param products[] An array of Products
   * @param inventoryProvider An instantiated object of data-provider::MagentoInventoryProvider
   */
  static async $updateInventory(products, inventoryProvider) {
    await inventoryProvider.fetchInventory(products.map(product => product.id));
    products.forEach(product => {
      product.variants.forEach(variant => {
        const quantity = inventoryProvider.getVariantQuantity(product.id, variant.id);
        if (quantity !== null) {
          variant.inventory = quantity;
        }
      });
    });
  }

  /**
   * Update the `discountPrice` and `variants[].inventory attributes of the given array of products with the given personalizedProductProvider object
   * @param products[] An array of Products
   * @param personalizedProductProvider An instantiated object of data-provider::PersonalizedProductProvider
   */
  static async $personalizeProducts(products, personalizedProductProvider) {
    await personalizedProductProvider.fetchPersonalProducts(products.map(product => product.id));
    return products.map(product => {
      const discountPrice = personalizedProductProvider.getPrice(product.id);
      const shouldUpdateDiscountPrice = discountPrice !== null && discountPrice < product.price && discountPrice >= 0;
      const variants = product.variants.map(variant => {
        const quantity = personalizedProductProvider.getVariantQuantity(product.id, variant.id);
        return _objectSpread2(_objectSpread2({}, variant), {}, {
          inventory: quantity ? quantity : variant === null || variant === void 0 ? void 0 : variant.inventory
        });
      });
      return new product.constructor(_objectSpread2(_objectSpread2({}, product), {}, {
        discountPrice: shouldUpdateDiscountPrice ? discountPrice : product.discountPrice,
        variants
      }));
    });
  }

  /**
   *
   * @param products
   * @param originalPriceProvider
   */
  static async $updateOutletPrices(products, originalPriceProvider) {
    await originalPriceProvider.fetchPrices();
    products.forEach(product => {
      const originalPrice = originalPriceProvider.getOriginalPrice(product.id);
      if (originalPrice !== null && product.price !== originalPrice) {
        product.discountPrice = product.price;
        product.price = originalPrice;
      }
    });
  }
}

class CatalogProduct extends Product {
  static $fields() {
    return _objectSpread2(_objectSpread2({}, super.$fields()), {}, {
      mainImage: this.$relateOne(Image),
      marketingName: this.$string(),
      // Atom LT Hoody Men's or Veste Alpha SV Femme (translated)
      description: this.$string(),
      shortDescription: this.$string(),
      isNew: this.$boolean(false),
      isRevised: this.$boolean(false),
      isPro: this.$boolean(false),
      minPrice: this.$number(),
      minDiscountPrice: this.$number(),
      rating: this.$number(),
      reviewCount: this.$number()
    });
  }

  /**
   * @return if price has a min and max price range. For example: $50 - $200 returns true.
   */
  get hasPriceRange() {
    const [minPriceRange, maxPriceRange] = this.priceRange;
    return Boolean(minPriceRange) && Boolean(maxPriceRange);
  }

  /**
   * @return true if the variant/colour price is discounted. Assumption: All variants of the same colour has the same price.
   * In the future, if sizes of the same colour varies, retain only the code for selectedVariant.
   */
  get isPriceDiscounted() {
    const variant = this.selectedVariant;
    if (variant) {
      return Boolean(variant.discountPrice);
    }

    // if no variant has been selected, we use the article information
    if (this.selectedColour) {
      var _this$variants;
      // this assumes that all sizes of the same colour have the same price
      const [variant] = (_this$variants = this.variants) === null || _this$variants === void 0 ? void 0 : _this$variants.filter(variant => {
        var _this$selectedColour;
        return variant.colourId === ((_this$selectedColour = this.selectedColour) === null || _this$selectedColour === void 0 ? void 0 : _this$selectedColour.value);
      });
      return Boolean(variant === null || variant === void 0 ? void 0 : variant.discountPrice);
    }
  }

  /**
   * @return the price range for the product
   */
  get priceRange() {
    const {
      discountPrice,
      minPrice,
      minDiscountPrice,
      price: regularPrice
    } = this;
    const showPriceRange = Boolean(minDiscountPrice && minDiscountPrice < minPrice && minDiscountPrice !== minPrice && minDiscountPrice !== discountPrice);
    const minPriceRange = minDiscountPrice;
    const maxPriceRange = showPriceRange ? discountPrice || regularPrice : null;
    return [minPriceRange, maxPriceRange];
  }

  /**
   * @return the absolute price range differences. For example: min price: 700, max price: 800 - return 100
   */
  get priceRangeDifference() {
    const {
      price,
      minPrice,
      discountPrice,
      minDiscountPrice
    } = this;
    if (minPrice && price > minPrice) {
      return price - minPrice;
    } else if (discountPrice && minDiscountPrice && discountPrice > minDiscountPrice) {
      return discountPrice - minDiscountPrice;
    } else if (price && minDiscountPrice && price > minDiscountPrice) {
      return price - minDiscountPrice;
    }
    return price - discountPrice;
  }
  get priceRangePercentage() {
    return this.priceRangeDifference ? Math.round(this.priceRangeDifference / this.price * 1000) / 10 : 0;
  }
  /**
   * @return an array of variants for the selected colour
   */
  get colourVariants() {
    var _this$colourOptions, _this$variants2;
    const colourId = (_this$colourOptions = this.colourOptions) === null || _this$colourOptions === void 0 ? void 0 : _this$colourOptions.selected;
    return colourId ? (_this$variants2 = this.variants) === null || _this$variants2 === void 0 ? void 0 : _this$variants2.filter(variant => variant.colourId === colourId) : [];
  }

  /**
   * @return [min, max] of discounted price values on selected colour's variants
   * - [0, 0] for regular priced article
   * - min & max will have the same value if the article has the same discounted price across all its variants
   */
  get colourVariantsPriceRange() {
    const variants = this.colourVariants.map(item => item.discountPrice).filter(item => item !== null);
    // there is no discount for all variants on regular priced colour/article
    if (variants.length === 0) {
      return [0, 0];
    }
    const min = Math.min(...variants);
    const max = Math.max(...variants);
    return [min, max];
  }
  updateInventory(productInventory) {
    this.variants.forEach(variant => {
      const inventoryItem = productInventory.lookup(variant.id);
      variant.inventory = (inventoryItem === null || inventoryItem === void 0 ? void 0 : inventoryItem.quantity) || 0;
      variant.availableIn = (inventoryItem === null || inventoryItem === void 0 ? void 0 : inventoryItem.availableIn) || 0;
    });
  }
}

class ProductTile extends CatalogProduct {
  static $fields() {
    return _objectSpread2(_objectSpread2({}, super.$fields()), {}, {
      rating: this.$number(),
      reviewCount: this.$number()
    });
  }
}

class MatchPreference extends CatalogProduct {
  static $fields() {
    return {
      name: this.$string(),
      match: this.$boolean(),
      highlight: this.$boolean()
    };
  }
}

class CartfulProductTile extends ProductTile {
  static $fields() {
    return _objectSpread2(_objectSpread2({}, super.$fields()), {}, {
      score: this.$number(),
      matchText: this.$string(),
      matchPreferences: this.$relateMany(MatchPreference)
    });
  }
}

class CustomerTip extends Model {
  static $fields() {
    return {
      label: this.$string(),
      value: this.$string()
    };
  }
}

class Link extends Model {
  static $fields() {
    return {
      label: this.$string(),
      url: this.$string(),
      meta: this.$attr(),
      analytics_label: this.$string()
    };
  }
}

class Video extends Model {
  static $fields() {
    return {
      label: this.$attr(),
      url: this.$attr(),
      coverImageUrl: this.$attr(),
      colourLabel: this.$string()
    };
  }
}

class MiniWidget extends Model {
  static $fields() {
    return {
      title: this.$string(),
      description: this.$string(),
      iconUrl: this.$string()
    };
  }
}

class BigWidget extends Model {
  static $fields() {
    return {
      id: this.$string(),
      // ex: mens-hiking-and-trekking-pants
      label: this.$string(),
      // ex: Men's Comparison Hiking & Treking (translated for Accordion label)
      type: this.$string(),
      // ex: fit-guide
      html: this.$html() // For TAS only - should be deprecated once we rip HTML out
    };
  }
}

class TechnicalInfoWidget extends Model {
  static $fields() {
    return {
      ctaImageUrl: this.$string(),
      ctaText: this.$string(),
      ctaTextUrl: this.$string(),
      description: this.$string(),
      imageUrl: this.$string(),
      title: this.$string()
    };
  }
}

// Used for when translated labels come from provider
class TranslatedAttribute extends Model {
  static $fields() {
    return {
      label: this.$string(),
      value: this.$attr()
    };
  }
}

class KeyFeature extends Model {
  static $fields() {
    return {
      description: this.$string(),
      imageUrl: this.$string(),
      title: this.$string() // ex: Men's Comparison Hiking & Treking (translated for Accordion label and content title)
    };
  }
}

class ProductPDP extends CatalogProduct {
  static $fields() {
    return _objectSpread2(_objectSpread2({}, super.$fields()), {}, {
      genderCategory: this.$string(),
      // Men's | Women's
      customerTips: this.$relateOne(CustomerTip),
      isUnisex: this.$boolean(),
      brochureMode: this.$boolean(),
      brochureMode2: this.$boolean(),
      category: this.$string(),
      // Shell Jackets
      subCategory: this.$string(),
      origYear: this.$string(),
      origSeason: this.$string(),
      miniWidgets: this.$relateMany(MiniWidget),
      sizingChart: this.$relateOne(Link),
      conversionVideo: this.$relateOne(Video),
      campaignVideo: this.$relateOne(Video),
      careVideo: this.$relateOne(Video),
      manufacturingFacility: this.$relateOne(Link),
      ancillaryFacilities: this.$relateMany(Link),
      modelMetrics: this.$array(),
      modelMetricsExtra: this.$array(),
      size: this.$relateOne(TranslatedAttribute),
      weight: this.$relateOne(TranslatedAttribute),
      fit: this.$string(),
      activities: this.$array(),
      climate: this.$string(),
      features: this.$relateMany(TranslatedAttribute),
      bigWidgets: this.$relateMany(BigWidget),
      materials: this.$array(),
      careInstructions: this.$array(),
      awards: this.$array(),
      detailedImages: this.$relateMany(Image),
      detailedVideos: this.$relateMany(Video),
      hidePrice: this.$boolean(),
      keyFeatures: this.$relateMany(KeyFeature),
      breadcrumbs: this.$relateMany(Link),
      miscellaneousPrimary: this.$array(),
      miscellaneousExtra: this.$array(),
      technicalInfoWidgets: this.$relateMany(TechnicalInfoWidget)
    });
  }
}

// write a function that would prepend a hashtag to a hex code if it doesn't already have one
const prependHash = hexCode => {
  if (hexCode && hexCode[0] !== "#") {
    return `#${hexCode}`;
  }
  return hexCode;
};
const isDarkColourImage = colourCode => {
  // remove the first character if it's a hash
  const hexCode = colourCode.replace("#", "");
  if (hexCode) {
    const r = parseInt(hexCode.substr(0, 2), 16);
    const g = parseInt(hexCode.substr(2, 2), 16);
    const b = parseInt(hexCode.substr(4, 2), 16);
    const yiq = (r * 299 + g * 587 + b * 114) / 1000;
    return yiq <= 92;
  }
  return false;
};

// This is to accommodate an over-sight with how we get the "Gender Category" (Men's, Women's, Unisex).
// We had assumed this translation was in the database along with the Translated Name, etc, but it's
// actually calculated in Legacy Ecom app. So our Frontend expects the translated value to come from the
// "backend", when really we should have handled the translation on the frontend. To make the output of
// the Magento PDP Adapter 1:1 consistent TAS Adapter, we'll handle translation of gender here, but future
// refactor could fix this and move it to Catalog Pages repo.
//
// Note that that if we wanted to fix this now, we'd have to re-implement the TAS Adapter as well as the
// frontend UI. I believe this work-around is path of least resistance at this stage.
const translations = {
  men: {
    en: "Men's",
    fr: "Homme",
    de: "Herren",
    jp: "メンズ",
    es: "Hombre",
    zh: "男装",
    no: "Herre"
  },
  women: {
    en: "Women's",
    fr: "Femme",
    de: "Damen",
    jp: "ウィメンズ",
    es: "Mujer",
    zh: "女装",
    no: "Dame"
  }
};
const translateGender = (key, lang) => {
  var _translations$key;
  return (translations === null || translations === void 0 ? void 0 : (_translations$key = translations[key]) === null || _translations$key === void 0 ? void 0 : _translations$key[lang]) || "";
};
const translateWeightLabel = lang => {
  const weightLabelTranslations = {
    en: "Weight",
    fr: "Poids",
    de: "Gewicht",
    jp: "重量",
    es: "Peso",
    zh: "重量",
    no: "vekt"
  };
  return weightLabelTranslations[lang] || weightLabelTranslations.en;
};
const collectionLabels = {
  pro: "Pro",
  veilance: "Veilance",
  system_a: "System A"
};
const getLabelFromCollectionCode = code => collectionLabels[code] || "";
const adaptVideoHtml = html => {
  if (!html) {
    return null;
  }
  const {
    label,
    videoURL: url,
    thumbnailURL: coverImageUrl,
    videotitle: videoTitle
  } = html;
  return {
    label,
    url,
    coverImageUrl,
    videoTitle
  };
};
const parseJson = function (str) {
  let fallback = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : [];
  try {
    return JSON.parse(str) || [];
  } catch (e) {
    return fallback;
  }
};

/*
 * This deals with strings of html that we want to convert into an array of items.
 * html string that contain list items (<li>bla</li>) come with a paragraph label that we don't care about
 * however, some strings come with a single paragraph, which we need (with no lists)
 */
const normalizeMagentoHtml = cleanMe => {
  let sanitizedContent = [];
  if (cleanMe.length) {
    var _cleanMe$;
    const contentToClean = (_cleanMe$ = cleanMe[0]) === null || _cleanMe$ === void 0 ? void 0 : _cleanMe$.LegacyContents;
    const contentContainsListItems = contentToClean === null || contentToClean === void 0 ? void 0 : contentToClean.includes("<li>");
    const newLineStart = contentContainsListItems ? /<li>/g : /<p>/g;
    const itemStartTag = contentContainsListItems ? "<li>" : "<p>";
    const tagToSplitOn = contentContainsListItems ? "</li>" : "</p>";

    // if we have a list of items, we don't need this label
    const htmlString = contentContainsListItems ? contentToClean.replace(/<p>(.*)?<\/p>/, "") : contentToClean;
    sanitizedContent = striptags(htmlString, itemStartTag).replace(newLineStart, "").split(tagToSplitOn).map(c => c.trim()).filter(x => x);
  }
  return sanitizedContent;
};

/**
 *
 * @param {String} obj - ex. '[{"label":"Machine wash in cold water. ","sortorder":"1500","careinstructions":"Machine wash in cold water. ","magento_option_id":"272"},{"label":"Surface Clean Only (SURF CLEAN)","sortorder":"1400","careinstructions":"Surface clean only.","magento_option_id":"306"}]'
 * @param {String} attribute - property name to select - ex. "careinstructions"
 * @returns an array of attribute values sorted in descending order - ex. [ 'Machine wash in cold water. ', 'Surface clean only.' ]
 */
const adaptToArray = (obj, attribute) => {
  if (!obj) return [];
  const array = parseJson(obj, []);

  // the LegacyContents attribute contains an html string (deprecated)
  if (array.length && array[0].LegacyContents) {
    return normalizeMagentoHtml(array);
  } else {
    // sort in descending order
    return array === null || array === void 0 ? void 0 : array.sort((a, b) => parseInt(a.sortorder, 10) < parseInt(b.sortorder, 10) ? 1 : parseInt(a.sortorder, 10) > parseInt(b.sortorder, 10) ? -1 : 0).map(a => a[attribute]);
  }
};
const adaptModelMetrics = modelMetrics => {
  var _modelMetrics$;
  if (!modelMetrics) return [];
  if ((_modelMetrics$ = modelMetrics[0]) !== null && _modelMetrics$ !== void 0 && _modelMetrics$.LegacyContents) {
    return normalizeMagentoHtml(modelMetrics);
  }
  return modelMetrics.map(item => {
    const {
      height,
      chestwidth: chestWidth,
      waist,
      wearingsize: wearingSize,
      inseam,
      hip
    } = item;
    const result = {
      height,
      chestWidth,
      waist,
      wearingSize,
      inseam,
      hip
    };
    return Object.entries(result).filter(_ref => {
      let [key, value] = _ref;
      return value !== undefined;
    }).map(_ref2 => {
      let [key, value] = _ref2;
      return `${key.charAt(0) + key.slice(1)}: ${value}`;
    });
  });
};
const adaptConversionVideo = videoJson => {
  var _Object$values;
  let conversionVideo = null;
  const videoData = (_Object$values = Object.values(parseJson(videoJson))) === null || _Object$values === void 0 ? void 0 : _Object$values[0];
  if (videoData) {
    const {
      label = "",
      videoid: videoId = "",
      specialCode = "autoplay=1",
      previewimage: coverImageUrl = ""
    } = videoData;
    conversionVideo = {
      label,
      url: `//youtube.com/embed/${videoId}?${specialCode}`,
      coverImageUrl
    };
  }
  return conversionVideo;
};
const isValid = data => data && Array.isArray(data);
const adaptKeyFeatures = data => {
  return !isValid(data) ? [] : data.map(item => {
    const {
      description,
      imageurl: imageUrl,
      label: title
    } = item;
    return {
      description,
      imageUrl,
      title
    };
  });
};
const adaptMiscWidget = miscJson => {
  var _miscJson$;
  if (!isValid(miscJson)) {
    return [];
  }
  if ((_miscJson$ = miscJson[0]) !== null && _miscJson$ !== void 0 && _miscJson$.LegacyContents) {
    return normalizeMagentoHtml(miscJson);
  }
  return miscJson.map(item => {
    const {
      ctaimageurl: ctaImageUrl,
      ctatext: ctaText,
      ctatexturl: ctaTextUrl,
      description,
      imageurl: imageUrl,
      label
    } = item;
    return {
      ctaImageUrl,
      ctaText,
      ctaTextUrl,
      description,
      imageUrl,
      label
    };
  });
};
const adaptTechnicalInfoWidgets = data => {
  return !isValid(data) ? [] : data.map(item => {
    const {
      ctaimageurl: ctaImageUrl,
      ctatext: ctaText,
      ctatexturl: ctaTextUrl,
      description,
      imageurl: imageUrl,
      label: title
    } = item;
    return {
      ctaImageUrl,
      ctaText,
      ctaTextUrl,
      description,
      imageUrl,
      title
    };
  });
};
const adaptCustomerTips = (countryCode, data) => {
  var _customerTips$allowed;
  if (!data) {
    return {};
  }
  const [customerTips] = data;
  const showTips = customerTips === null || customerTips === void 0 ? void 0 : (_customerTips$allowed = customerTips.allowedcountries) === null || _customerTips$allowed === void 0 ? void 0 : _customerTips$allowed.map(countryLabel => {
    var _countryLabel$split$;
    return (_countryLabel$split$ = countryLabel.split(" - ")[0]) === null || _countryLabel$split$ === void 0 ? void 0 : _countryLabel$split$.toLowerCase();
  }).includes(countryCode);
  if (showTips) {
    const {
      customerservicetip: value,
      label
    } = customerTips;
    return {
      value,
      label
    };
  }
  return {};
};
const footwearConversionSizeTableFromUK = {
  us: {
    men: {
      3.5: "4",
      4: "4.5",
      4.5: "5",
      5: "5.5",
      5.5: "6",
      6: "6.5",
      6.5: "7",
      7: "7.5",
      7.5: "8",
      8: "8.5",
      8.5: "9",
      9: "9.5",
      9.5: "10",
      10: "10.5",
      10.5: "11",
      11: "11.5",
      11.5: "12",
      12: "12.5",
      12.5: "13",
      13: "13.5",
      13.5: "14"
    },
    women: {
      3.5: "5",
      4: "5.5",
      4.5: "6",
      5: "6.5",
      5.5: "7",
      6: "7.5",
      6.5: "8",
      7: "8.5",
      7.5: "9",
      8: "9.5",
      8.5: "10",
      9: "10.5",
      9.5: "11",
      10: "11.5",
      10.5: "12",
      11: "12.5",
      11.5: "13",
      12: "13.5",
      12.5: "14",
      13: "14.5",
      13.5: "15"
    }
  },
  eur: {
    3.5: "36",
    4: "36 2/3",
    4.5: "37 1/3",
    5: "38",
    5.5: "38 2/3",
    6: "39 1/3",
    6.5: "40",
    7: "40 2/3",
    7.5: "41 1/3",
    8: "42",
    8.5: "42 2/3",
    9: "43 1/3",
    9.5: "44",
    10: "44 2/3",
    10.5: "45 1/3",
    11: "46",
    11.5: "46 2/3",
    12: "47 1/3",
    12.5: "48",
    13: "48 2/3",
    13.5: "49 1/3"
  }
};
const sizeSchemes = {
  at: "eur",
  be: "eur",
  ca: "us",
  ch: "eur",
  cn: "uk",
  cz: "eur",
  de: "eur",
  dk: "eur",
  es: "eur",
  fi: "eur",
  fr: "eur",
  gb: "uk",
  ie: "eur",
  it: "eur",
  nl: "eur",
  no: "eur",
  pl: "eur",
  se: "eur",
  us: "us"
};
const gramsToLbs = weightInGrams => {
  const weightInKg = weightInGrams / 1000;
  const nearExact = weightInKg / 0.45359237;
  const lbs = Math.floor(nearExact);
  const lbsLabel = lbs > 0 ? `${lbs} lb` : ``;
  const ounces = (nearExact - lbs) * 16;
  const oz = Math.round(ounces * 10) / 10;
  const ozLabel = oz > 0 ? `${oz} oz` : ``;
  return lbsLabel || ozLabel ? `${lbsLabel} ${ozLabel}`.trim() : null;
};
const capitalize = string => string === null || string === void 0 ? void 0 : string.replace(/(^\w|\xC9|\xE9|\w|\s\w)(\S*\/|\S*)/g, (_, m1, m2) => (m1 === null || m1 === void 0 ? void 0 : m1.toUpperCase()) + (m2 === null || m2 === void 0 ? void 0 : m2.toLowerCase()));
function capitalizeManufacturingLabel(string) {
  if (!string || typeof string !== "string") return "";
  const businessCodes = ["LTD", "YY", "FG", "CEPZ"];
  let capitalizedString = capitalize(string);

  // Business rule to capitalize first character after a "."
  capitalizedString = capitalizedString.split(".").map((str, index) => {
    if (index > 0) {
      return capitalize(str);
    }
    return str;
  }).join(".");

  // Make sure letters after a hyphen "-" are capitalized
  capitalizedString = capitalizedString.replace(/(-\w)/g, (_, m1) => m1.toUpperCase());

  // Ensure certain business codes are capitalized
  businessCodes.forEach(businessCode => {
    const re = new RegExp(businessCode, "ig");
    capitalizedString = capitalizedString.replace(re, businessCode);
  });
  return capitalizedString;
}
const adaptImageUrl = url => {
  const newUrl = typeof url === "undefined" || !url ? "https://images-dynamic-arcteryx.imgix.net/pdp/placeholder.jpg" : url;
  const urlObj = new URL(newUrl.startsWith("http") ? newUrl : `https:${newUrl}`);
  const {
    origin = "",
    pathname = ""
  } = urlObj;
  return {
    pathname,
    url: `${origin}${decodeURI(pathname)}`
  };
};

const adaptSkuBadges = (badgeData, country) => {
  if (country) {
    const FIRST_ITEM = 0;
    const regionalBadges = JSON.parse(badgeData) || [];
    return regionalBadges.filter(item => {
      var _item$countries;
      const countryCodes = (_item$countries = item.countries) === null || _item$countries === void 0 ? void 0 : _item$countries.map(c => c.split("_")[FIRST_ITEM].toLowerCase());
      return countryCodes === null || countryCodes === void 0 ? void 0 : countryCodes.includes(country);
    }).map(_ref => {
      let {
        englishlabel,
        label: title,
        translatedlabel
      } = _ref;
      return {
        code: englishlabel,
        label: translatedlabel || (title === null || title === void 0 ? void 0 : title.split(" - ")[FIRST_ITEM]) || ""
      };
    });
  }
  return [];
};

// Helper function used in cart.js maps magento discount_notice to info message, else return null
const getPromoInfoMessage = promoMessage => {
  var _InfoMessageMap$find;
  const InfoMessageMap = [{
    infoName: "PROMO-INFO-SOME-INELIGIBLE",
    infoPattern: /Promo code is not applicable for all items/
  }];
  return promoMessage ? (_InfoMessageMap$find = InfoMessageMap.find(msg => promoMessage.match(msg.infoPattern))) === null || _InfoMessageMap$find === void 0 ? void 0 : _InfoMessageMap$find.infoName : null;
};

// Helper function used in cart.js maps magento promo errors <Array> to error message, else return null
const getPromoErrorMessage = errors => {
  const ErrorMessageMap = [{
    errorName: 'PROMO-ERROR-INELIGIBLE',
    errorPattern: /The promo code you entered does not apply to the product\(s\) in the cart/
  }, {
    errorName: 'PROMO-ERROR-NONEXIST',
    errorPattern: /Promo code does not exist/
  }, {
    errorName: 'PROMO-ERROR-EXPIRED',
    errorPattern: /Promo code is expired/
  }, {
    errorName: 'PROMO-FAILURE-MESSAGE',
    errorPattern: "The coupon code isn't valid. Verify the code and try again."
  }];
  let returnErrorPromo = null;
  for (const errorMessage of ErrorMessageMap) {
    const invalidPromoCode = errors.filter(_ref2 => {
      let {
        message
      } = _ref2;
      return message.match(errorMessage.errorPattern);
    }).length === 1;
    if (invalidPromoCode) {
      returnErrorPromo = {
        isValid: false,
        validationMessage: errorMessage.errorName
      };
    }
  }
  return returnErrorPromo;
};

// will import it from data-models
const adaptImage$1 = function () {
  let {
    label,
    reals3url: url,
    ColorLabel: colourLabel
  } = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : {};
  if (!url) {
    return {
      label,
      url: '',
      pathname: '',
      colourLabel: ''
    };
  }
  const {
    origin = '',
    pathname = ''
  } = new URL(`${(url === null || url === void 0 ? void 0 : url.indexOf('http')) !== 0 ? 'https:' : ''}${url}`);
  return {
    label,
    url: `${origin}${decodeURI(pathname)}`,
    pathname,
    colourLabel
  };
};

const FLAT = 'Flat';
const FRONT = 'Front';
const FRONT_VIEW = 'Front-View';
const PROFILE = 'Profile';
const SIDE_VIEW = 'Side-View';

const categoryImageViews = {
  'Accessories - Gloves': FRONT,
  'Accessories - Hats & Caps': FRONT_VIEW,
  'Accessories - Hats and Caps': FRONT_VIEW,
  'Accessories - More': FRONT,
  'Accessories - Product Care': FRONT,
  'Accessories - Socks': FRONT_VIEW,
  'Accessories - Toques & Beanies': FRONT_VIEW,
  'Accessories - Toques and Beanies': FRONT_VIEW,
  'Base Layer': FRONT_VIEW,
  'Climbing Gear': FRONT_VIEW,
  'Dresses': FRONT_VIEW,
  'Dresses and Skirts - Dresses': FRONT_VIEW,
  'Fleece': FRONT_VIEW,
  'Footwear': PROFILE,
  'Insulated Jackets': FRONT_VIEW,
  'One-Piece': FRONT,
  'Other': FLAT,
  'Packs': FRONT_VIEW,
  'Pants': FRONT_VIEW,
  'Shell Jackets': FRONT_VIEW,
  'Shirts and Tops': FRONT_VIEW,
  'Shorts': FRONT_VIEW
};
const categoryImageViewsFallback = {
  'Other': FRONT,
  'Footwear - Boots': FRONT,
  'Footwear - Shoes': FRONT,
  'Accessories - Socks': SIDE_VIEW
};
const findImageView = (category, subCategory, imageViews) => {
  const key = `${category} - ${subCategory}`;
  return imageViews[key] || imageViews[category];
};
const getImageView = (category, subCategory) => {
  return findImageView(category, subCategory, categoryImageViews);
};
const getImageViewFallback = (category, subCategory) => {
  return findImageView(category, subCategory, categoryImageViewsFallback);
};

/**
 * Finds the product's hero image based on its alternate views, categories, subcategories, and colour label.
 *
 * @param {Object} options - An object containing the following properties:
 * @param {Object} options.images - An object representing array of alternate views for the product.
 * @param {string} options.categories - A comma-separated string of product categories.
 * @param {string} options.subCategories - A comma-separated string of subcategories.
 * @param {string} options.colorLabel - The color label to match the image.
 * @returns {Object|undefined} The adapted hero image object, or undefined if no images are provided or found.
 */
const getHeroImage = _ref => {
  var _categories$split, _subCategories$split;
  let {
    images,
    categories,
    subCategories,
    colorLabel
  } = _ref;
  if (!images) {
    return undefined;
  }
  // take the first category if product has multiple categories
  // ex. Rho Hybrid Crew Neck LS (Shirts and Tops, Base Layer)
  const category = categories === null || categories === void 0 ? void 0 : (_categories$split = categories.split(',')) === null || _categories$split === void 0 ? void 0 : _categories$split[0];
  const subCategory = subCategories === null || subCategories === void 0 ? void 0 : (_subCategories$split = subCategories.split(',')) === null || _subCategories$split === void 0 ? void 0 : _subCategories$split[0];

  // get the default image view type for this product category [subcategory] - Front, Flat, etc.
  const imageView = getImageView(category, subCategory);
  // while we're at it, let's try searching for a fallback image view type also
  const imageViewFallback = getImageViewFallback(category, subCategory);

  // convert images object to an array for easier searching
  const imagesArray = Object.values(images);
  // find a matching image based on the colour & image view / fallback image view
  const matchingItem = imagesArray.find(image => {
    var _image$reals3url;
    return image.ColorLabel === colorLabel && ((_image$reals3url = image.reals3url) === null || _image$reals3url === void 0 ? void 0 : _image$reals3url.toLowerCase().includes(imageView === null || imageView === void 0 ? void 0 : imageView.toLowerCase()));
  });
  const matchingFallbackItem = imagesArray.find(image => {
    var _image$reals3url2;
    return image.ColorLabel === colorLabel && ((_image$reals3url2 = image.reals3url) === null || _image$reals3url2 === void 0 ? void 0 : _image$reals3url2.toLowerCase().includes(imageViewFallback === null || imageViewFallback === void 0 ? void 0 : imageViewFallback.toLowerCase()));
  });

  // adapt image to expected data structure
  let matchedItem;
  if (matchingItem) {
    matchedItem = adaptImage$1(matchingItem);
  } else if (matchingFallbackItem) {
    matchedItem = adaptImage$1(matchingFallbackItem);
  }

  // return undefined if no matching image is found
  return matchedItem;
};

const adaptImage = function () {
  let {
    label,
    url,
    colourLabel = ""
  } = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : {};
  return {
    label,
    url: url || "https://images.arcteryx.com/pdp/placeholder.jpg",
    pathname: url ? new URL(url).pathname : "/pdp/placeholder.jpg",
    colourLabel: colourLabel || ""
  };
};

// @sizeLocal - The displayed region specific size
// @sizeScheme - us|uk|eur
// @gender - men|women
// @return sizeValue
const normalizeMagentoSizeValue = (sizeLocal, sizeScheme, gender) => {
  let sizeValue = sizeLocal;
  if (sizeScheme) {
    // This is a footwear size
    // SKUs use UK sizing, map to region sizing
    if (gender === "neutral" && sizeScheme === "us") {
      // Unisex shoes
      // These are stored as mens UK size, but we convert to US sizing and show womens size too
      const mensSize = footwearConversionSizeTableFromUK.us.men[sizeLocal];
      const womensSize = footwearConversionSizeTableFromUK.us.women[sizeLocal];
      sizeValue = `${mensSize}M`;
      if (womensSize) {
        sizeValue = `${sizeValue}/${footwearConversionSizeTableFromUK.us.women[sizeLocal]}W`;
      }
    } else {
      // convert all other shoe sizes to local equivalent
      if (sizeScheme === "us" && (gender === "men" || gender === "women")) {
        sizeValue = footwearConversionSizeTableFromUK.us[gender][sizeLocal];
      } else if (sizeScheme === "eur") {
        sizeValue = footwearConversionSizeTableFromUK.eur[sizeLocal];
      }
    }
  }
  return sizeValue;
};
const adaptBaseProduct = (data, market, country) => {
  const {
    sku: id,
    slug,
    marketingname: name,
    marketingnameenglish: analyticsName,
    price_range: {
      maximum_price: {
        regular_price: {
          value: price,
          currency: currencyCode
        } = {},
        final_price: {
          value: finalPrice
        } = {},
        discount: {
          amount_off: discountAmount = 0
        } = {}
      } = {}
    } = {},
    gendercode,
    productcollectioncode,
    is_returnable,
    isfootwear: isFootwear,
    isnewsizing: isNewSizing
  } = data;
  let discountPrice = null;
  if (discountAmount > 0) {
    discountPrice = finalPrice;
  }
  const sizeScheme = country && isFootwear ? sizeSchemes[country.toLowerCase()] : null;
  return {
    id,
    slug,
    name,
    analyticsName: `${analyticsName} ${translateGender(gendercode, "en")}`.trim(),
    market,
    currencyCode,
    price,
    discountPrice,
    gender: gendercode === "neutral" ? "unisex" : gendercode,
    collection: productcollectioncode,
    collectionLabel: getLabelFromCollectionCode(productcollectioncode),
    isReturnable: is_returnable === "1" || is_returnable === "2",
    sizeScheme,
    isFootwear,
    isNewSizing
  };
};
const adaptCatalogProduct = (data, baseProduct, genderCategory) => {
  var _detailedImages$, _detailedImages$2, _detailedImages$3, _data$price_range, _minPriceRange$regula, _minPriceRange$discou;
  const {
    marketingname: baseMarketingName,
    marketingnameenglish: analyticsName,
    long_description: description,
    short_description: {
      html: shortDescription
    } = {},
    alternateviews = null,
    videosnippets,
    isnew = false,
    isrevised = false
  } = data;
  const marketingName = baseMarketingName ? `${baseMarketingName} ${genderCategory}`.trim() : null;
  let detailedImages = Object.values(parseJson(alternateviews, {}));
  detailedImages = detailedImages.map(_ref => {
    let {
      reals3url: url,
      label,
      ColorLabel: colourLabel = ""
    } = _ref;
    return adaptImage({
      label,
      url,
      colourLabel
    });
  });
  let detailedVideos = Object.values(parseJson(videosnippets, {}));
  detailedVideos = detailedVideos.map(_ref2 => {
    let {
      reals3url: url,
      label,
      ColorLabel: colourLabel = ""
    } = _ref2;
    const fullUrl = new URL(url);
    return {
      url: `${fullUrl === null || fullUrl === void 0 ? void 0 : fullUrl.origin}${fullUrl === null || fullUrl === void 0 ? void 0 : fullUrl.pathname}`,
      label,
      coverImageUrl: null,
      colourLabel
    };
  });
  const mainImage = {
    label: analyticsName,
    url: ((_detailedImages$ = detailedImages[0]) === null || _detailedImages$ === void 0 ? void 0 : _detailedImages$.url) || "",
    pathname: ((_detailedImages$2 = detailedImages[0]) === null || _detailedImages$2 === void 0 ? void 0 : _detailedImages$2.pathname) || "",
    colourLabel: ((_detailedImages$3 = detailedImages[0]) === null || _detailedImages$3 === void 0 ? void 0 : _detailedImages$3.colourLabel) || ""
  };
  const minPriceRange = (_data$price_range = data.price_range) === null || _data$price_range === void 0 ? void 0 : _data$price_range.minimum_price;
  const minPrice = (minPriceRange === null || minPriceRange === void 0 ? void 0 : (_minPriceRange$regula = minPriceRange.regular_price) === null || _minPriceRange$regula === void 0 ? void 0 : _minPriceRange$regula.value) || (baseProduct === null || baseProduct === void 0 ? void 0 : baseProduct.price);
  let minDiscountPrice = null;
  if ((minPriceRange === null || minPriceRange === void 0 ? void 0 : (_minPriceRange$discou = minPriceRange.discount) === null || _minPriceRange$discou === void 0 ? void 0 : _minPriceRange$discou.amount_off) > 0) {
    var _minPriceRange$final_;
    minDiscountPrice = minPriceRange === null || minPriceRange === void 0 ? void 0 : (_minPriceRange$final_ = minPriceRange.final_price) === null || _minPriceRange$final_ === void 0 ? void 0 : _minPriceRange$final_.value;
  }
  return {
    mainImage,
    marketingName,
    description,
    shortDescription,
    isNew: Boolean(isnew),
    isRevised: Boolean(isrevised),
    isPro: baseProduct.collection === "pro" && baseProduct.market !== "leaf",
    detailedImages,
    detailedVideos,
    minPrice,
    minDiscountPrice
  };
};
const adaptCartProduct = (data, errors, isReserved) => {
  var _configured_variant$q;
  const {
    id: cartItemId,
    quantity: initalQuantity,
    is_available,
    configured_variant,
    arc_sap_sourcing: sapSource = "",
    arc_original_country_price: regularPrice,
    prices: {
      price: {
        value: cartPrice,
        currency: currencyCode
      } = {},
      discounts = []
    } = {},
    still_todo: {
      isGift
    } = {}
  } = data;

  // outdoor prices
  let price = cartPrice;
  let discountPrice = null;
  let promoDiscount = null;
  let catalogDiscount = null;

  // outlet and special user prices
  if (cartPrice > 0 && cartPrice < regularPrice) {
    price = regularPrice;
    discountPrice = cartPrice;
  }

  // promo discount on product for analytic purposes
  const promoLineDiscount = Array.isArray(discounts) ? discounts.filter(discount => {
    var _discount$label;
    return ((_discount$label = discount.label) === null || _discount$label === void 0 ? void 0 : _discount$label.toLowerCase()) === "discount";
  }) : [];
  if (promoLineDiscount.length === 1) {
    promoDiscount = promoLineDiscount[0].amount.value;
  }
  const catalogLineDiscount = Array.isArray(discounts) ? discounts.filter(discount => {
    var _discount$label2;
    return ((_discount$label2 = discount.label) === null || _discount$label2 === void 0 ? void 0 : _discount$label2.toLowerCase()) === "catalog_price_rule";
  }) : [];
  if (catalogLineDiscount.length === 1) {
    catalogDiscount = catalogLineDiscount[0].amount.value;
  }

  // DEFAULT
  let stockMessage = "";
  let isOutOfStock = false;
  let quantity = initalQuantity;

  // LESS-THAN-5-LEFT
  if (configured_variant.quantity_available < 5) {
    stockMessage = "LESS-THAN-5-LEFT";
  }

  // WRONG-QUANTITY
  if (data.customError) {
    stockMessage = data.customError;
  }

  // OUT-OF-STOCK
  configured_variant.quantity_available = (_configured_variant$q = configured_variant.quantity_available) !== null && _configured_variant$q !== void 0 ? _configured_variant$q : 0;
  if (!isReserved && configured_variant.quantity_available === 0 || price === 0 || !is_available) {
    stockMessage = "NO-LONGER-AVAILABLE";
    isOutOfStock = true;
    quantity = 0;
  }
  errors.forEach(error => {
    // WRONG-QUANTITY
    if (error.message.match(`Could not update the product with SKU ${configured_variant.sku}: The requested qty is not available`) || error.message.match(`Could not add the product with SKU ${configured_variant.sku} to the shopping cart: The requested qty is not available`)) {
      stockMessage = "WRONG-QUANTITY";
    }
  });
  return {
    cartItemId,
    quantity,
    price,
    discountPrice,
    promoDiscount,
    catalogDiscount,
    currencyCode,
    stockMessage,
    isOutOfStock,
    isGift,
    sapSource
  };
};
const cleanSizeLabel = dirtySizeLabel => {
  return dirtySizeLabel.trim().replace("-", "").replace("Regular", "Reg").replace("Short", "Srt");
};
const adaptConfigurableProduct = _ref3 => {
  var _sizeValues, _sizeValues2, _colourOptionsOptions;
  let {
    country = null,
    simpleProducts,
    colourValues,
    sizeValues,
    selectedColourId = null,
    genderCode = null,
    sizeScheme,
    isFootwear = 0,
    alternateViews,
    categoryEnglish: categories,
    subCategoryEnglish: subCategories
  } = _ref3;
  const variants = [];
  const colourMeta = {};
  const sizeMeta = {};
  simpleProducts.forEach(_ref4 => {
    var _JSON$parse, _JSON$parse2;
    let {
      product: {
        sku: id,
        badge_data = null,
        color_data = null,
        size_data = null,
        quantity_available: inventory,
        productimage: colorThumbnail = "",
        price_range: {
          maximum_price: {
            regular_price: {
              value: price,
              currency: currencyCode
            } = {},
            final_price: {
              value: finalPrice
            } = {},
            discount: {
              amount_off: discountAmount = 0
            } = {}
          } = {}
        } = {},
        upc
      }
    } = _ref4;
    const FIRST_ITEM = 0;
    const colourData = ((_JSON$parse = JSON.parse(color_data)) === null || _JSON$parse === void 0 ? void 0 : _JSON$parse[FIRST_ITEM]) || {};
    const sizeData = ((_JSON$parse2 = JSON.parse(size_data)) === null || _JSON$parse2 === void 0 ? void 0 : _JSON$parse2[FIRST_ITEM]) || {};
    const badges = adaptSkuBadges(badge_data, country);
    const {
      magento_option_id: colourId,
      HexValue: hexCode = "",
      hexValueSecondary = "",
      BaseColor: primaryColour = "",
      label: colorLabel
    } = colourData;
    const {
      Size2Value: subSizeLabel /* eslint-disable-line no-unused-vars */,
      magento_option_id: sizeId,
      General_SortOrder: order
    } = sizeData;

    // TODO: these will eventually come from magento as cleaned, but they're blocking tickets
    let {
      Size1Value: sizeLabel,
      SubSizeCd: subSizeCode
    } = sizeData;
    subSizeCode = subSizeCode ? cleanSizeLabel(subSizeCode) : subSizeCode;
    sizeLabel = sizeLabel ? cleanSizeLabel(sizeLabel) : sizeLabel;
    const normalizedSizeLabel = isFootwear ? normalizeMagentoSizeValue(sizeLabel, sizeScheme, genderCode) : sizeLabel;
    const label = subSizeCode !== undefined ? `${normalizedSizeLabel}-${subSizeCode}` : normalizedSizeLabel;
    if (!colourMeta[colourId]) {
      var _Object$values;
      // transparent image - off model image
      const colorThumbnailData = ((_Object$values = Object.values(parseJson(colorThumbnail, {}))) === null || _Object$values === void 0 ? void 0 : _Object$values[0]) || {};
      const {
        label: imageLabel = "",
        reals3url: imagePath = ""
      } = colorThumbnailData;
      const thumbnailImage = adaptImage({
        label: imageLabel,
        url: imagePath
      });
      const heroImage = getHeroImage({
        images: alternateViews,
        categories,
        subCategories,
        colorLabel
      });
      colourMeta[colourId] = {
        hexCode,
        hexValueSecondary,
        primaryColour,
        image: thumbnailImage,
        thumbnail: thumbnailImage,
        heroImage
      };
    }
    sizeMeta[sizeId] = {
      label,
      order,
      sizeLabel: normalizedSizeLabel
    };
    let discountPrice = null;
    if (discountAmount > 0) {
      discountPrice = finalPrice;
    }
    variants.push({
      badges,
      id,
      upc,
      colourId,
      sizeId,
      inventory,
      currencyCode,
      price,
      discountPrice
    });
  });
  const filteredColourValues = colourValues === null || colourValues === void 0 ? void 0 : colourValues.map(_ref5 => {
    let {
      value_index,
      store_label
    } = _ref5;
    return {
      value_index,
      label: store_label === null || store_label === void 0 ? void 0 : store_label.trim()
    };
  });
  const colourOptionsOptions = (filteredColourValues === null || filteredColourValues === void 0 ? void 0 : filteredColourValues.map(_ref6 => {
    let {
      label,
      value_index
    } = _ref6;
    const value = `${value_index}`; // we want string values to maintain consistent types
    const {
      hexCode,
      hexValueSecondary,
      primaryColour,
      image,
      thumbnail,
      heroImage
    } = colourMeta[value] || {};
    const hexValueWithHash = prependHash(hexCode);
    const hexValueSecondaryWithHash = prependHash(hexValueSecondary);
    return !colourMeta[value] ? {} : {
      heroImage,
      hexCode: hexValueWithHash,
      hexValueSecondary: hexValueSecondaryWithHash,
      image,
      isDark: isDarkColourImage(hexCode),
      label,
      primaryColour: primaryColour === null || primaryColour === void 0 ? void 0 : primaryColour.toLowerCase(),
      thumbnail,
      value
    };
  }).filter(n => n.label)) || [];
  sizeValues = (_sizeValues = sizeValues) === null || _sizeValues === void 0 ? void 0 : _sizeValues.sort((a, b) => parseInt(a.value_index, 10) > parseInt(b.value_index, 10) ? 1 : parseInt(b.value_index, 10) > parseInt(a.value_index, 10) ? -1 : 0);
  const sizeOptionsOptions = ((_sizeValues2 = sizeValues) === null || _sizeValues2 === void 0 ? void 0 : _sizeValues2.map(_ref7 => {
    let {
      value_index
    } = _ref7;
    const value = `${value_index}`; // we want string values to maintain consistent types
    const {
      label,
      sizeLabel,
      order
    } = sizeMeta[value] || {};
    return !sizeMeta[value] ? {} : {
      label,
      value,
      sizeLabel,
      order
    };
  }).filter(n => n.label)) || [];
  sizeOptionsOptions.sort((a, b) => parseInt(a.order, 10) > parseInt(b.order, 10) ? 1 : parseInt(b.order, 10) > parseInt(a.order, 10) ? -1 : 0);
  const colourOptions = {
    selected: selectedColourId || (colourOptionsOptions === null || colourOptionsOptions === void 0 ? void 0 : (_colourOptionsOptions = colourOptionsOptions[0]) === null || _colourOptionsOptions === void 0 ? void 0 : _colourOptionsOptions.value),
    options: colourOptionsOptions
  };
  const sizeOptions = {
    selected: sizeOptionsOptions.length === 1 ? sizeOptionsOptions[0].value : null,
    options: sizeOptionsOptions
  };
  return {
    variants,
    colourOptions,
    sizeOptions
  };
};
const adaptProductAggregations = aggregations => {
  const features = {};

  // we want these attributes returned as a comma separated list
  const attributesToJoin = ["fit", "originalseason", "climate", "sizetype"];
  const attributesToIgnore = ["price", "category_id", "color", "gender", "intendeduse", "jacketfinder_hoodtype", "jacketfinder_temprating", "footwearfinder_activity", "footwearfinder_crampon", "footwearfinder_cushioning", "footwearfinder_feature", "footwearfinder_grip", "footwearfinder_intention", "footwearfinder_stability", "footwearfinder_support", "footwearfinder_terrain", "footwearfinder_weather", "productcollection", "webcollection", "webmaterials", "webcollection_bucket"];
  aggregations.forEach(_ref8 => {
    let {
      attribute_code,
      label,
      options
    } = _ref8;
    if (!attributesToIgnore.includes(attribute_code)) {
      const attributeOptions = options.map(_ref9 => {
        let {
          label
        } = _ref9;
        return label;
      });
      const attributeOptionsEnglish = options.map(_ref10 => {
        let {
          label_en
        } = _ref10;
        return label_en;
      });
      features[attribute_code] = attributesToJoin.includes(attribute_code) ? attributeOptions.join(`, `) : {
        label,
        value: attributeOptions,
        valueEnglish: attributeOptionsEnglish,
        attributeCode: attribute_code
      };
    }
  });
  return features;
};

const _excluded = ["baseWeight"],
  _excluded2 = ["baseWeight"],
  _excluded3 = ["fit", "originalseason", "webcategory", "websubcategory", "webactivities", "climate", "sizetype"];
const adaptBigWidgetData = (bigWidgetJson, country, language) => {
  const adaptedWidgets = [];
  const widgetData = parseJson(bigWidgetJson);
  if (widgetData.length) {
    widgetData.forEach(bigWidget => {
      let widgetType = "generic";
      if (bigWidget !== null && bigWidget !== void 0 && bigWidget.LegacyContents.includes("fit-guide")) {
        widgetType = "fit-guide";
      } else if (bigWidget !== null && bigWidget !== void 0 && bigWidget.LegacyContents.includes("layering")) {
        widgetType = "layering";
      }
      const widgetContent = (bigWidget === null || bigWidget === void 0 ? void 0 : bigWidget.LegacyContents.replace("/$country$/$lang$/", `/${country}/${language}/`)) || "";
      adaptedWidgets.push({
        id: "",
        type: widgetType,
        label: (bigWidget === null || bigWidget === void 0 ? void 0 : bigWidget.label) || "",
        html: widgetContent
      });
    });
  }
  return adaptedWidgets;
};
const adaptmanufacturingFacility = facilityJson => {
  const manufacturingFacilityData = parseJson(facilityJson, []);
  const {
    label: facilityLabel = "",
    url = "",
    FactoryAddress: address = ""
  } = (manufacturingFacilityData === null || manufacturingFacilityData === void 0 ? void 0 : manufacturingFacilityData[0]) || {};
  const label = address ? `${facilityLabel}, ${address}` : facilityLabel;
  const capitalizedLabel = capitalizeManufacturingLabel(label);
  return {
    label: capitalizedLabel,
    url
  };
};

// Return parsed breadcrumbs
const adaptBreadcrumbs = (breadcrumbsJson, baseProduct, genderLabel) => {
  const breadcrumbs = [];
  const breadcrumbsData = parseJson(breadcrumbsJson, []);
  let genderUrl = "";
  const isUnisex = baseProduct.gender === "unisex";
  if (!isUnisex) {
    genderUrl = baseProduct.gender === "men" ? "/mens" : "/womens";
  }

  // Collection breadcrumbs
  if (baseProduct.collection) {
    const isVeilance = baseProduct.collection === "veilance";
    const collectionSlug = baseProduct.collection === "pro" ? "professional-use" : baseProduct.collection;
    breadcrumbs.push({
      label: baseProduct.collectionLabel,
      url: isVeilance ? `/${collectionSlug}` : `/c/${collectionSlug}`
    });

    // Gender breadcrumb - /c/[gender]/[collection]
    if (!isUnisex) {
      breadcrumbs.push({
        label: genderLabel,
        url: `/c${genderUrl}/${collectionSlug}`
      });
    }
  } else {
    // Market breadcrumb - hardcode crumb label for now
    const marketLabel = baseProduct.market === "sale" ? "Arc'teryx Outlet" : baseProduct.market === "leaf" ? "Arc'teryx LEAF" : "Arc'teryx";
    breadcrumbs.push({
      label: marketLabel,
      url: "/"
    });

    // Gender breadcrumb - /c/[gender]
    if (!isUnisex) {
      breadcrumbs.push({
        label: genderLabel,
        url: `/c${genderUrl}`
      });
    }

    // When breadcrumbs are set in Magento - /c(/[gender])/category
    if (breadcrumbsData.length) {
      for (const breadcrumb of breadcrumbsData) {
        const {
          label,
          slug
        } = breadcrumb;
        const url = `/c${genderUrl}/${slug}`;
        breadcrumbs.push({
          label,
          url
        });
      }
    }
  }

  // Product breadcrumb - #
  breadcrumbs.push({
    label: baseProduct.name,
    url: "#"
  });
  return breadcrumbs;
};
const adaptPackWeight = (weights, volumes) => {
  var _Object$values$filter;
  const gramsToOz = 0.035274;
  let {
      baseWeight: weightInGrams
    } = weights,
    weightOverrides = _objectWithoutProperties(weights, _excluded);
  const shouldAddVolume = Object.values(volumes).some(x => x !== null);
  weightInGrams = parseInt((_Object$values$filter = Object.values(weightOverrides).filter(w => w)) === null || _Object$values$filter === void 0 ? void 0 : _Object$values$filter[0], 10);
  let volumeLabel = "";
  if (shouldAddVolume) {
    var _Object$values$filter2;
    const volumeValue = (_Object$values$filter2 = Object.values(volumes).filter(w => w)) === null || _Object$values$filter2 === void 0 ? void 0 : _Object$values$filter2[0];
    volumeLabel = ` / ${parseInt(volumeValue, 10)}L`;
  }
  const weightUnit = weightInGrams >= 1000 ? `kg` : `g`;
  const weightValue = weightInGrams >= 1000 ? weightInGrams / 1000 : weightInGrams;
  const weightValueLabel = `${weightValue}${weightUnit} / ${Math.round(weightInGrams * gramsToOz)}oz${volumeLabel}`;
  return weightInGrams && parseInt(weightInGrams, 10) > 0 ? weightValueLabel : null;
};

/**
 * weight comes from magento as a gram value, which we convert here into lbs/oz
 */
const adaptWeight = (weights, volumes, language) => {
  let {
      baseWeight: weightInGrams
    } = weights,
    weightOverrides = _objectWithoutProperties(weights, _excluded2);
  const weightUnit = weightInGrams >= 1000 ? `kg` : `g`;
  const weightValue = weightInGrams >= 1000 ? weightInGrams / 1000 : weightInGrams;
  let weightValueLabel = `${weightValue}${weightUnit} / ${gramsToLbs(weightInGrams)}`;

  // backpacks and footwear have separate weight values, provided in other attributes
  const shouldOverrideWeight = Object.values(weightOverrides).some(x => x !== null);
  if (shouldOverrideWeight) {
    var _Object$values$filter3;
    weightInGrams = parseInt((_Object$values$filter3 = Object.values(weightOverrides).filter(w => w !== null)) === null || _Object$values$filter3 === void 0 ? void 0 : _Object$values$filter3[0], 10);
    weightValueLabel = adaptPackWeight(weights, volumes);
  }
  return weightInGrams && parseInt(weightInGrams, 10) > 0 ? {
    label: translateWeightLabel(language),
    value: weightValueLabel
  } : null;
};
const adaptAwards = awardsData => {
  return parseJson(awardsData).map(_ref => {
    let {
      label: awardLabel,
      LinkLabel: awardLinkLabel,
      LinkUrl: awardLinkHref
    } = _ref;
    return awardLinkHref && awardLinkLabel ? `${awardLabel} - <a href="${awardLinkHref}">${awardLinkLabel}</a>` : awardLabel;
  });
};
const adaptFeatures = fullFeatures => {
  // sort features alphabetically, but always float technicalfeatures to the top
  let features = Object.keys(fullFeatures).map(key => ({
    label: fullFeatures[key].label,
    value: fullFeatures[key].value,
    key
  }));
  features.sort((a, b) => a.label > b.label ? 1 : b.label > a.label ? -1 : 0);

  // these attributes come through as 'features' but we display them in the 'materials' section
  const materialsAttributesToRemove = ["goretex", "materials", "windstopper", "polartec", "harness", "packbody", "packsuspension"];
  let materials = [];
  features.forEach(feature => {
    const featureToRemove = materialsAttributesToRemove.includes(feature.key);
    if (featureToRemove) {
      materials = materials.concat((feature === null || feature === void 0 ? void 0 : feature.value) || []);
      features = features.filter(thing => thing.key !== feature.key);
    }
  });
  return {
    features,
    materials
  };
};
const adaptMaterialsC8 = (materialsString, originalYearString) => {
  const originalYear = parseInt(originalYearString, 10);

  // materials_c8 will only be used for products with original year 2024 or later
  if (isNaN(originalYear) || originalYear < 2024) {
    return [];
  }
  const materials = parseJson(materialsString, []);
  return materials.map(_ref2 => {
    let {
      label,
      placement,
      materiallabel: materialLabel,
      materialcomposition: materialComposition
    } = _ref2;
    return {
      label,
      placement,
      materialLabel,
      materialComposition
    };
  });
};
const combineMaterials = (materialsOrig, materialsC8) => [...materialsC8.map(_ref3 => {
  let {
    placement,
    materialLabel,
    materialComposition
  } = _ref3;
  return materialComposition ? `${placement}: ${materialLabel} - ${materialComposition}` : `${placement}: ${materialLabel}`;
}), ...materialsOrig];
const htmlTagsAllowedInTextFields = ["p", "a", "em", "strong", "i", "b"];
const adaptPDPProduct = _ref4 => {
  var _data$products, _data$products2, _parseJson$map, _originalseason$split, _configurableProduct$, _configurableProduct$2;
  let {
    data,
    market,
    country,
    language
  } = _ref4;
  const magentoProduct = (data === null || data === void 0 ? void 0 : (_data$products = data.products) === null || _data$products === void 0 ? void 0 : _data$products.items[0]) || {};
  const productAggregations = (data === null || data === void 0 ? void 0 : (_data$products2 = data.products) === null || _data$products2 === void 0 ? void 0 : _data$products2.aggregations) || [];
  const {
    alternateviews = null,
    awardswidget_data: awardsData = [],
    gendercode: gender,
    miniwidget_data: miniWidgetJson = null,
    keyfeatureswidget_data: keyFeaturesJson = [],
    othermaterialdescription,
    shortdescription,
    longdescription,
    originalyear = null,
    sizingchart_data: sizingChartData = null,
    carevideowidget_data,
    campaignwidget_data,
    layeringwidget_tasmb_data: layeringWidgetJson,
    compareproductswidget_tasmb_data: compareWidgetJson,
    customerservicetipswidget_data: customerTipsJson = [],
    materials_c8_data: materialsString = null,
    modelmetric_primary_data: modelMetricsJson,
    modelmetric_secondary_data: modelMetricsExtraJson,
    carewidget_data: careInstructionsJson,
    manufacturingfacility_data: manufacturingFacilityJson,
    ancillarymanufacturingfacility_data: ancillaryManufacturingFacilityJson,
    breadcrumbs_data: breadcrumbsJson,
    brochuremode: brochureMode = false,
    brochuremode2: brochureMode2 = false,
    weight: baseWeight = null,
    footwearweight = null,
    packweightsml = null,
    packweighttall = null,
    packweightshort = null,
    packweightregular = null,
    packweightonesize = null,
    packvolumetall = null,
    packvolumeshort = null,
    packvolumeonesize = null,
    packvolumeregular = null,
    packvolumeextendedonesize = null,
    technicalinfowidget_data,
    conversionvideos: conversionVideoJson = null,
    hideprice: hidePrice = false,
    miscellaneouswidget_data: miscellaneousWidgetJson,
    configurable_options = [],
    variants: simpleProducts = []
  } = magentoProduct;
  const weights = {
    baseWeight,
    footwearweight,
    packweightsml,
    packweighttall,
    packweightshort,
    packweightregular,
    packweightonesize
  };
  const volumes = {
    packvolumetall,
    packvolumeshort,
    packvolumeonesize,
    packvolumeregular,
    packvolumeextendedonesize
  };
  const weight = adaptWeight(weights, volumes, language);
  const {
    values: sizeValues,
    label: sizeLabel
  } = configurable_options.find(opt => opt.attribute_code === "size") || {};
  const {
    use_default: selectedColourId,
    values: colourValues
  } = configurable_options.find(opt => opt.attribute_code === "color") || {};
  const allFeatures = adaptProductAggregations(productAggregations);
  const {
      fit,
      originalseason,
      webcategory,
      websubcategory,
      webactivities: activitiesOptions,
      climate = null,
      sizetype: sizeType
    } = allFeatures,
    fullFeatures = _objectWithoutProperties(allFeatures, _excluded3);
  const category = webcategory === null || webcategory === void 0 ? void 0 : webcategory.value.join(", ");
  const subCategory = websubcategory === null || websubcategory === void 0 ? void 0 : websubcategory.value.join(", ");
  const categoryEnglish = webcategory === null || webcategory === void 0 ? void 0 : webcategory.valueEnglish.join(", ");
  const subCategoryEnglish = websubcategory === null || websubcategory === void 0 ? void 0 : websubcategory.valueEnglish.join(", ");
  const genderCategory = translateGender(gender, language);
  const baseProduct = adaptBaseProduct(magentoProduct, market, country);
  const catalogProduct = adaptCatalogProduct(magentoProduct, baseProduct, genderCategory);
  const configurableProduct = adaptConfigurableProduct({
    country,
    simpleProducts,
    colourValues,
    sizeValues,
    selectedColourId,
    genderCode: gender,
    sizeType,
    isFootwear: baseProduct.isFootwear,
    sizeScheme: baseProduct.sizeScheme,
    alternateViews: JSON.parse(alternateviews) || {},
    categoryEnglish,
    subCategoryEnglish
  });
  const awards = adaptAwards(awardsData);
  const miniWidgets = parseJson(miniWidgetJson).map(_ref5 => {
    let {
      label: widgetTitle,
      Description: widgetDescription,
      LinkUrl: iconUrl
    } = _ref5;
    return {
      title: widgetTitle,
      description: widgetDescription,
      iconUrl
    };
  });
  const keyFeatures = adaptKeyFeatures(parseJson(keyFeaturesJson));
  const sizingChart = (_parseJson$map = parseJson(sizingChartData).map(_ref6 => {
    let {
      label,
      link_url: url
    } = _ref6;
    return {
      label,
      url
    };
  })) === null || _parseJson$map === void 0 ? void 0 : _parseJson$map[0];
  const isUnisex = gender === "neutral";
  const description = striptags(longdescription, htmlTagsAllowedInTextFields);
  const shortDescription = striptags(shortdescription, htmlTagsAllowedInTextFields);
  const origYear = originalyear;
  const careVideo = adaptVideoHtml(parseJson(carevideowidget_data, {})[0] || "");
  const campaignVideo = adaptVideoHtml(parseJson(campaignwidget_data, {})[0] || "");
  const careInstructions = adaptToArray(careInstructionsJson, "careinstructions");
  const origSeason = (originalseason === null || originalseason === void 0 ? void 0 : (_originalseason$split = originalseason.split("_")) === null || _originalseason$split === void 0 ? void 0 : _originalseason$split[1]) || "";
  const technicalInfoWidgets = adaptTechnicalInfoWidgets(parseJson(technicalinfowidget_data));
  let {
    features,
    materials
  } = adaptFeatures(fullFeatures);
  const techFeatures = features.find(feature => feature.key === "technicalfeatures");
  if (techFeatures) {
    features = features.filter(feature => feature.key !== "technicalfeatures");
    features.unshift(techFeatures);
  }
  const modelMetrics = adaptModelMetrics(parseJson(modelMetricsJson));
  const modelMetricsExtra = normalizeMagentoHtml(parseJson(modelMetricsExtraJson));
  const customerTips = adaptCustomerTips(country, parseJson(customerTipsJson));
  const compareWidgets = adaptBigWidgetData(compareWidgetJson, country, language);
  const layeringWidgets = adaptBigWidgetData(layeringWidgetJson, country, language);
  const bigWidgets = [...compareWidgets, ...layeringWidgets];
  const sizeOptionValues = [...new Set((_configurableProduct$ = configurableProduct.sizeOptions) === null || _configurableProduct$ === void 0 ? void 0 : (_configurableProduct$2 = _configurableProduct$.options) === null || _configurableProduct$2 === void 0 ? void 0 : _configurableProduct$2.map(_ref7 => {
    let {
      sizeLabel
    } = _ref7;
    return sizeLabel;
  }))];
  const manufacturingFacility = adaptmanufacturingFacility(manufacturingFacilityJson);
  const ancillaryFacilities = ancillaryManufacturingFacilityJson ? [adaptmanufacturingFacility(ancillaryManufacturingFacilityJson)] : [];
  const breadcrumbs = adaptBreadcrumbs(breadcrumbsJson, baseProduct, genderCategory);
  const conversionVideo = adaptConversionVideo(conversionVideoJson);

  // Add othermaterialdescription as part of materials.
  if (othermaterialdescription) {
    materials.push(othermaterialdescription);
  }
  // Add materials from C8
  const materialsC8 = adaptMaterialsC8(materialsString, originalyear);
  materials = combineMaterials(materials, materialsC8);
  const miscellaneousWidget = parseJson(miscellaneousWidgetJson) || "";
  const [miscPrimary, miscSecondary] = adaptMiscWidget(miscellaneousWidget);
  const miscellaneousPrimary = _objectSpread2({}, miscPrimary);
  const miscellaneousExtra = _objectSpread2({}, miscSecondary);
  return _objectSpread2(_objectSpread2(_objectSpread2(_objectSpread2({}, baseProduct), catalogProduct), configurableProduct), {}, {
    size: {
      label: sizeLabel,
      value: sizeOptionValues.join(", ") || null
    },
    genderCategory,
    customerTips,
    isUnisex,
    category,
    subCategory,
    origYear,
    origSeason,
    shortDescription,
    description,
    miniWidgets,
    sizingChart,
    conversionVideo,
    campaignVideo,
    careVideo,
    manufacturingFacility,
    ancillaryFacilities,
    modelMetrics,
    modelMetricsExtra,
    weight,
    fit,
    climate,
    activities: (activitiesOptions === null || activitiesOptions === void 0 ? void 0 : activitiesOptions.value) || [],
    features,
    bigWidgets,
    materials,
    careInstructions,
    awards,
    brochureMode,
    brochureMode2,
    hidePrice: Boolean(hidePrice),
    keyFeatures,
    breadcrumbs,
    miscellaneousPrimary,
    miscellaneousExtra,
    technicalInfoWidgets
  });
};

const adaptCartAddress = address => {
  const {
    firstname: firstName,
    lastname: lastName,
    city,
    postcode: postalCode,
    country: {
      code: countryCode
    } = {},
    street: [address1, address2] = [],
    telephone: phone,
    region: {
      code: regionCode,
      region_id
    } = {},
    is_po_box,
    pickup_location_code: pickupStoreCode
  } = address;
  return {
    address1,
    address2,
    city,
    region: regionCode,
    regionId: region_id,
    postalCode,
    country: countryCode,
    poBox: Boolean(is_po_box),
    firstName,
    lastName,
    phone,
    pickupStoreCode
  };
};

const naCountries = new Set(["US", "CA"]);
const adaptCartProducts = function () {
  let items = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : [];
  let errors = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : [];
  let market = arguments.length > 2 && arguments[2] !== undefined ? arguments[2] : "outdoor";
  let country = arguments.length > 3 && arguments[3] !== undefined ? arguments[3] : "";
  let isReserved = arguments.length > 4 && arguments[4] !== undefined ? arguments[4] : false;
  if (items.length <= 0) {
    return [];
  }
  return items.filter(item => item !== null).map(item => {
    const {
      product,
      configured_variant
    } = item;
    const {
      gendercode: genderCode = null
    } = product;
    const [colorSelected = {}] = JSON.parse(configured_variant.color_data) || [];
    const [sizeSelected = {}] = JSON.parse(configured_variant.size_data) || [];
    const baseProduct = adaptBaseProduct(product, market, country);
    const cartProduct = adaptCartProduct(item, errors, isReserved);
    const configurableProduct = configured_variant ? adaptConfigurableProduct({
      simpleProducts: [{
        product: configured_variant
      }],
      colourValues: [{
        store_label: colorSelected.label,
        value_index: colorSelected.magento_option_id
      }],
      sizeValues: [{
        value_index: sizeSelected.magento_option_id
      }],
      genderCode,
      isFootwear: baseProduct.isFootwear,
      sizeScheme: baseProduct.sizeScheme
    }) : {};
    return _objectSpread2(_objectSpread2(_objectSpread2({}, baseProduct), cartProduct), configurableProduct);
  });
};
const adaptTaxTotal = taxArr => {
  let taxTotal = 0;
  if (Array.isArray(taxArr) && taxArr !== null && taxArr !== void 0 && taxArr.length) {
    taxTotal = taxArr.reduce((acc, obj) => {
      var _obj$amount;
      return acc + (obj === null || obj === void 0 ? void 0 : (_obj$amount = obj.amount) === null || _obj$amount === void 0 ? void 0 : _obj$amount.value);
    }, 0);
  }
  return taxTotal;
};
const adaptTaxTotalPercentage = taxArr => {
  let taxPercentTotal = 0;
  if (Array.isArray(taxArr) && taxArr !== null && taxArr !== void 0 && taxArr.length) {
    taxPercentTotal = taxArr.reduce((acc, obj) => acc + (obj === null || obj === void 0 ? void 0 : obj.percent), 0);
  }
  return taxPercentTotal;
};
const findOutOfStockItems = cart => cart === null || cart === void 0 ? void 0 : cart.items.filter(item => {
  if (item !== null) {
    var _item$configured_vari, _item$configured_vari2;
    const quantity_available = (_item$configured_vari = item === null || item === void 0 ? void 0 : (_item$configured_vari2 = item.configured_variant) === null || _item$configured_vari2 === void 0 ? void 0 : _item$configured_vari2.quantity_available) !== null && _item$configured_vari !== void 0 ? _item$configured_vari : 0;
    return (item === null || item === void 0 ? void 0 : item.quantity) > 0 && quantity_available === 0 || !(item !== null && item !== void 0 && item.is_available);
  }
  return false;
});
const findZeroPriceItems = cart => {
  var _cart$items;
  return cart === null || cart === void 0 ? void 0 : (_cart$items = cart.items) === null || _cart$items === void 0 ? void 0 : _cart$items.filter(item => {
    var _item$prices, _item$prices$price;
    return (item === null || item === void 0 ? void 0 : (_item$prices = item.prices) === null || _item$prices === void 0 ? void 0 : (_item$prices$price = _item$prices.price) === null || _item$prices$price === void 0 ? void 0 : _item$prices$price.value) === 0 && (item === null || item === void 0 ? void 0 : item.is_available);
  });
};
const adaptShippingMethod = _ref => {
  let {
    available,
    carrier_code,
    carrier_title,
    error_message,
    method_code,
    method_title,
    amount,
    price_excl_tax,
    price_incl_tax
  } = _ref;
  return {
    available,
    carrierCode: carrier_code,
    carrierTitle: carrier_title,
    errorMessage: error_message,
    code: method_code,
    title: method_title,
    amount: amount === null || amount === void 0 ? void 0 : amount.value,
    priceWithTax: price_incl_tax === null || price_incl_tax === void 0 ? void 0 : price_incl_tax.value,
    priceWithoutTax: price_excl_tax === null || price_excl_tax === void 0 ? void 0 : price_excl_tax.value,
    currency: amount === null || amount === void 0 ? void 0 : amount.currency
  };
};
const adaptShippingBillingDetails = details => {
  const adaptedAddress = adaptCartAddress(details);
  const adaptedDetails = _objectSpread2(_objectSpread2({}, adaptedAddress), {}, {
    shippingAddress: adaptedAddress
  });
  if (details !== null && details !== void 0 && details.available_shipping_methods || details !== null && details !== void 0 && details.selected_shipping_method) {
    const {
      available_shipping_methods: availableShippingMethods,
      selected_shipping_method: selectedShippingMethod
    } = details;
    adaptedDetails.availableShippingMethods = availableShippingMethods !== null && availableShippingMethods !== void 0 && availableShippingMethods.length ? availableShippingMethods.map(method => adaptShippingMethod(method)) : null;
    adaptedDetails.selectedShippingMethod = selectedShippingMethod ? adaptShippingMethod(selectedShippingMethod) : null;
  }
  return adaptedDetails;
};
const adaptCartPromo = (cart, errors, isReserved) => {
  var _cart$applied_coupons;
  // handle invalid promo code
  if (Array.isArray(errors)) {
    const returnErrorPromo = getPromoErrorMessage(errors);
    if (returnErrorPromo) {
      return returnErrorPromo;
    }
  }

  // handle valid promo code
  if (cart !== null && cart !== void 0 && cart.applied_coupons && cart !== null && cart !== void 0 && (_cart$applied_coupons = cart.applied_coupons[0]) !== null && _cart$applied_coupons !== void 0 && _cart$applied_coupons.code) {
    var _cart$prices, _cart$prices$discount, _cart$prices$discount2, _cart$prices$discount3, _cart$applied_coupons2;
    let discountTotal = cart === null || cart === void 0 ? void 0 : (_cart$prices = cart.prices) === null || _cart$prices === void 0 ? void 0 : (_cart$prices$discount = _cart$prices.discounts) === null || _cart$prices$discount === void 0 ? void 0 : (_cart$prices$discount2 = _cart$prices$discount.filter(discount => {
      var _discount$label;
      return ((_discount$label = discount.label) === null || _discount$label === void 0 ? void 0 : _discount$label.toLowerCase()) === "discount";
    })[0]) === null || _cart$prices$discount2 === void 0 ? void 0 : (_cart$prices$discount3 = _cart$prices$discount2.amount) === null || _cart$prices$discount3 === void 0 ? void 0 : _cart$prices$discount3.value;
    if (!isReserved) {
      //recalculate promo discount if there are OOS products in the cart
      findOutOfStockItems(cart).forEach(item => {
        var _item$prices2, _item$prices2$discoun, _item$prices2$discoun2, _item$prices2$discoun3;
        discountTotal -= ((_item$prices2 = item.prices) === null || _item$prices2 === void 0 ? void 0 : (_item$prices2$discoun = _item$prices2.discounts) === null || _item$prices2$discoun === void 0 ? void 0 : (_item$prices2$discoun2 = _item$prices2$discoun.filter(discount => {
          var _discount$label2;
          return ((_discount$label2 = discount.label) === null || _discount$label2 === void 0 ? void 0 : _discount$label2.toLowerCase()) === "discount";
        })[0]) === null || _item$prices2$discoun2 === void 0 ? void 0 : (_item$prices2$discoun3 = _item$prices2$discoun2.amount) === null || _item$prices2$discoun3 === void 0 ? void 0 : _item$prices2$discoun3.value) || 0;
      });
    }
    return {
      isValid: true,
      promoCode: cart === null || cart === void 0 ? void 0 : (_cart$applied_coupons2 = cart.applied_coupons[0]) === null || _cart$applied_coupons2 === void 0 ? void 0 : _cart$applied_coupons2.code,
      discountTotal,
      validationMessage: "PROMO-SUCCESS-MESSAGE",
      promoInfoMessage: getPromoInfoMessage(cart === null || cart === void 0 ? void 0 : cart.discount_notice)
    };
  }
  // no promo
  return null;
};
const adaptCart = (_ref2, market, country, isReserved) => {
  var _cart$prices4, _cart$prices4$grand_t, _cart$shipping_addres, _cart$items2, _cart$prices5, _cart$prices5$grand_t, _cart$prices6, _cart$prices7, _cart$selected_paymen, _cart$selected_paymen2;
  let {
    cart,
    errors = []
  } = _ref2;
  let subtotalPrice = null;
  const hasNaCountries = naCountries.has(country === null || country === void 0 ? void 0 : country.toUpperCase());
  if (!hasNaCountries) {
    var _cart$prices2, _cart$prices2$subtota;
    // EMEA
    subtotalPrice = (cart === null || cart === void 0 ? void 0 : (_cart$prices2 = cart.prices) === null || _cart$prices2 === void 0 ? void 0 : (_cart$prices2$subtota = _cart$prices2.subtotal_including_tax) === null || _cart$prices2$subtota === void 0 ? void 0 : _cart$prices2$subtota.value) || null;
  }
  if (hasNaCountries) {
    var _cart$prices3, _cart$prices3$subtota;
    // NA
    subtotalPrice = (cart === null || cart === void 0 ? void 0 : (_cart$prices3 = cart.prices) === null || _cart$prices3 === void 0 ? void 0 : (_cart$prices3$subtota = _cart$prices3.subtotal_excluding_tax) === null || _cart$prices3$subtota === void 0 ? void 0 : _cart$prices3$subtota.value) || null;
  }
  let totalPrice = (cart === null || cart === void 0 ? void 0 : (_cart$prices4 = cart.prices) === null || _cart$prices4 === void 0 ? void 0 : (_cart$prices4$grand_t = _cart$prices4.grand_total) === null || _cart$prices4$grand_t === void 0 ? void 0 : _cart$prices4$grand_t.value) || null;
  let quantity = (cart === null || cart === void 0 ? void 0 : cart.total_quantity) || null;
  // there should only be one shipping address at any time in the cart response
  const shippingDetails = cart !== null && cart !== void 0 && (_cart$shipping_addres = cart.shipping_addresses) !== null && _cart$shipping_addres !== void 0 && _cart$shipping_addres.length ? adaptShippingBillingDetails(cart === null || cart === void 0 ? void 0 : cart.shipping_addresses[0]) : null;
  const billingAddress = cart !== null && cart !== void 0 && cart.billing_address ? adaptShippingBillingDetails(cart === null || cart === void 0 ? void 0 : cart.billing_address) : null;
  // recalculate total if there are OOS products in the cart
  if (!isReserved && (cart === null || cart === void 0 ? void 0 : (_cart$items2 = cart.items) === null || _cart$items2 === void 0 ? void 0 : _cart$items2.length) > 0) {
    findOutOfStockItems(cart).forEach(item => {
      var _item$prices3, _item$prices3$discoun, _item$prices3$discoun2, _item$prices3$discoun3, _item$prices4, _item$prices4$price, _item$prices5, _item$prices5$price;
      const itemDiscount = ((_item$prices3 = item.prices) === null || _item$prices3 === void 0 ? void 0 : (_item$prices3$discoun = _item$prices3.discounts) === null || _item$prices3$discoun === void 0 ? void 0 : (_item$prices3$discoun2 = _item$prices3$discoun.filter(discount => {
        var _discount$label3;
        return ((_discount$label3 = discount.label) === null || _discount$label3 === void 0 ? void 0 : _discount$label3.toLowerCase()) === "discount";
      })[0]) === null || _item$prices3$discoun2 === void 0 ? void 0 : (_item$prices3$discoun3 = _item$prices3$discoun2.amount) === null || _item$prices3$discoun3 === void 0 ? void 0 : _item$prices3$discoun3.value) || 0;
      subtotalPrice -= (item === null || item === void 0 ? void 0 : (_item$prices4 = item.prices) === null || _item$prices4 === void 0 ? void 0 : (_item$prices4$price = _item$prices4.price) === null || _item$prices4$price === void 0 ? void 0 : _item$prices4$price.value) * item.quantity || 0;
      totalPrice -= (item === null || item === void 0 ? void 0 : (_item$prices5 = item.prices) === null || _item$prices5 === void 0 ? void 0 : (_item$prices5$price = _item$prices5.price) === null || _item$prices5$price === void 0 ? void 0 : _item$prices5$price.value) * item.quantity - itemDiscount || 0;
      quantity -= item.quantity || 0;
    });
    findZeroPriceItems(cart).forEach(item => {
      quantity -= item.quantity || 0;
    });
  }
  return {
    id: cart === null || cart === void 0 ? void 0 : cart.id,
    email: cart === null || cart === void 0 ? void 0 : cart.email,
    currencyCode: (cart === null || cart === void 0 ? void 0 : (_cart$prices5 = cart.prices) === null || _cart$prices5 === void 0 ? void 0 : (_cart$prices5$grand_t = _cart$prices5.grand_total) === null || _cart$prices5$grand_t === void 0 ? void 0 : _cart$prices5$grand_t.currency) || null,
    subtotalPrice,
    totalPrice,
    tax: adaptTaxTotal(cart === null || cart === void 0 ? void 0 : (_cart$prices6 = cart.prices) === null || _cart$prices6 === void 0 ? void 0 : _cart$prices6.merged_taxes) || 0,
    taxPercentage: adaptTaxTotalPercentage(cart === null || cart === void 0 ? void 0 : (_cart$prices7 = cart.prices) === null || _cart$prices7 === void 0 ? void 0 : _cart$prices7.merged_taxes) || 0,
    quantity,
    products: adaptCartProducts(cart === null || cart === void 0 ? void 0 : cart.items, errors, market, country, isReserved),
    shippingDetails,
    billingAddress,
    selectedPaymentMethodCode: cart === null || cart === void 0 ? void 0 : (_cart$selected_paymen = cart.selected_payment_method) === null || _cart$selected_paymen === void 0 ? void 0 : _cart$selected_paymen.code,
    selectedPaymentMethodTitle: cart === null || cart === void 0 ? void 0 : (_cart$selected_paymen2 = cart.selected_payment_method) === null || _cart$selected_paymen2 === void 0 ? void 0 : _cart$selected_paymen2.title,
    promo: adaptCartPromo(cart, errors, isReserved),
    // Preserve errors returned from MagentoCartProvider so they are not swallowed by MagentoCart.$create
    errors
  };
};

/**
 * Return the product with exact identifier match and has non-empty variants (when variants are present).
 *
 * @param {Array} magentoItems array of Magento products
 * @param {String|Number} slugOrId product slug or sku
 * @returns {Object} Magento product object
 */
const pickMagentoProduct = _ref => {
  let {
    magentoItems = [],
    slugOrId
  } = _ref;
  const isCentricSku = slugOrId.length === 10 && slugOrId.indexOf("X") === 0;
  const isSlug = isNaN(slugOrId) && !isCentricSku ? true : false;
  const productIdentifier = isSlug ? "slug" : "sku";
  const filteredMagentoItem = magentoItems.find(item => item[productIdentifier] === slugOrId && (item.variants ? item.variants.length : true));
  return filteredMagentoItem || {};
};
const getProductAggregations = async _ref2 => {
  let {
    magentoData,
    provider,
    slugOrId
  } = _ref2;
  const magentoProduct = pickMagentoProduct({
    magentoItems: magentoData.products.items,
    slugOrId
  });
  magentoData.products.items = [magentoProduct];
  const extraMagentoData = await provider.request(gqlGetProductAggregationsBySku, {
    sku: magentoProduct.sku
  });
  if (extraMagentoData.products.aggregations.length) {
    magentoData.products.aggregations = extraMagentoData.products.aggregations;
  }
  return magentoData;
};
class MagentoProduct extends ProductPDP {
  /**
   *
   * @param {String} slug <required if sku undefined> The product url slug. ex: "mens/atom-lt-hoody"
   * @param {String} sku <required if slug undefined> The Configurable product SKU aka Product.id. ex: "27818"
   * @param {MagentoGraphqlProvider} provider <required> An instantiated MagentoGraphqlProvider object
   */
  static async $load(_ref3) {
    var _magentoData, _magentoData$products, _magentoData2, _magentoData2$product;
    let {
      slug,
      sku,
      provider
    } = _ref3;
    const {
      market,
      country,
      language
    } = provider;
    const query = slug ? gqlGetProductsBySlug : gqlGetProductsBySku;
    const variables = slug ? {
      slug: `\"${slug}\"`
    } : {
      sku
    }; // eslint-disable-line no-useless-escape
    const slugOrId = slug || sku;
    let magentoData = await provider.request(query, variables);

    /*
     * When we query gql for a slug, it does a text search, which for some slugs returns multiple items
     * eg: `womens/beta-lt-jacket` will match/return `womens/beta-lt-jacket` and `womens/beta-lt-jacket-hadron`
     * we use pickMagentoProduct() to select the correct one based on slug
     * But gql aggregations are the combination of BOTH products, so for queries with multiple products
     * we use the sku of the correct product to run a separate aggregate query for the correct data.
     */

    // only re-fetch aggregations for queries that originally asked for them, and those with multiple products
    const shouldRefetchAggregations = ((_magentoData = magentoData) === null || _magentoData === void 0 ? void 0 : (_magentoData$products = _magentoData.products) === null || _magentoData$products === void 0 ? void 0 : _magentoData$products.items.length) > 1 && ((_magentoData2 = magentoData) === null || _magentoData2 === void 0 ? void 0 : (_magentoData2$product = _magentoData2.products) === null || _magentoData2$product === void 0 ? void 0 : _magentoData2$product.aggregations.length) > 0;
    if (shouldRefetchAggregations) {
      magentoData = await getProductAggregations({
        magentoData,
        provider,
        slugOrId
      });
    }
    const pdpProduct = adaptPDPProduct({
      data: magentoData,
      market,
      country,
      language
    });
    return new ProductPDP(pdpProduct);
  }
}

class CartProduct extends Product {
  static $fields() {
    return _objectSpread2(_objectSpread2({}, super.$fields()), {}, {
      cartItemId: this.$string(),
      quantity: this.$number(),
      stockMessage: this.$string(),
      isOutOfStock: this.$boolean(),
      isGift: this.$boolean(),
      promoDiscount: this.$number(),
      catalogDiscount: this.$number(),
      sapSource: this.$string()
    });
  }
  get linePrice() {
    return this.price * this.quantity;
  }
  get lineDiscountPrice() {
    return this.discountPrice * this.quantity;
  }
  get linePromoPrice() {
    if (!this.linePrice || !this.promoDiscount) {
      return null;
    }
    if (this.discountPrice) {
      return this.lineDiscountPrice - this.promoDiscount;
    }
    return this.linePrice - this.promoDiscount;
  }
}

class Promo extends Model {
  static $fields() {
    return _objectSpread2(_objectSpread2({}, super.$fields()), {}, {
      isValid: this.$boolean(),
      promoCode: this.$string(),
      discountTotal: this.$number(),
      validationMessage: this.$string(),
      promoInfoMessage: this.$string()
    });
  }
}

class Address extends Model {
  static $fields() {
    return {
      address1: this.$string(),
      address2: this.$string(),
      city: this.$string(),
      region: this.$string(),
      regionId: this.$number(),
      postalCode: this.$string(),
      country: this.$string(),
      poBox: this.$boolean(),
      phone: this.$string(),
      pickupStoreCode: this.$string()
    };
  }
}

class CartAddress extends Address {
  static $fields() {
    return _objectSpread2(_objectSpread2({}, super.$fields()), {}, {
      firstName: this.$string(),
      lastName: this.$string(),
      phone: this.$string()
    });
  }
}

class SelectedShippingMethod extends Model {
  static $fields() {
    return _objectSpread2(_objectSpread2({}, super.$fields()), {}, {
      carrierCode: this.$string(),
      carrierTitle: this.$string(),
      code: this.$string(),
      title: this.$string(),
      amount: this.$number(),
      currency: this.$string()
    });
  }
}

class ShippingMethod extends SelectedShippingMethod {
  static $fields() {
    return _objectSpread2(_objectSpread2({}, super.$fields()), {}, {
      available: this.$boolean(),
      errorMessage: this.$string(),
      priceWithTax: this.$number(),
      priceWithoutTax: this.$number()
    });
  }
}

class ShippingDetails extends Model {
  static $fields() {
    return _objectSpread2(_objectSpread2({}, super.$fields()), {}, {
      shippingAddress: this.$relateOne(CartAddress),
      availableShippingMethods: this.$relateMany(ShippingMethod),
      selectedShippingMethod: this.$relateOne(SelectedShippingMethod)
    });
  }
}

class Cart extends Model {
  static $fields() {
    return _objectSpread2(_objectSpread2({}, super.$fields()), {}, {
      id: this.$string(),
      email: this.$string(),
      currencyCode: this.$string(),
      subtotalPrice: this.$number(),
      totalPrice: this.$number(),
      tax: this.$number(),
      taxPercentage: this.$number(),
      quantity: this.$number(),
      products: this.$relateMany(CartProduct),
      promo: this.$relateOne(Promo),
      shippingDetails: this.$relateOne(ShippingDetails),
      billingAddress: this.$relateOne(CartAddress),
      selectedPaymentMethodCode: this.$string(),
      selectedPaymentMethodTitle: this.$string(),
      isReserved: this.$boolean(),
      errors: this.$array()
    });
  }
  get totalSavings() {
    var _this$promo;
    let productSavings = this.products ? this.products.reduce((savings, product) => savings + product.savings * product.quantity, 0) : 0;
    if ((_this$promo = this.promo) !== null && _this$promo !== void 0 && _this$promo.isValid && this.promo.discountTotal) {
      productSavings += this.promo.discountTotal;
    }
    return productSavings;
  }
}

class MagentoCart extends Cart {
  static $create(cartData, market, country, isReserved) {
    return new MagentoCart(adaptCart(cartData, market, country, isReserved));
  }
}

class MagentoCartAddress extends CartAddress {
  static $create(address) {
    return new MagentoCartAddress(adaptCartAddress(address));
  }
}

class ProductInventory$1 extends Model {
  static $fields() {
    return {
      sku: this.$attr(),
      quantity: this.$number(),
      availableIn: this.$number() // weeks
    };
  }
}

/**
 * Formally describes the list of product inventory.
 * example: input
 * [{
 *   sku: 123,
 *   quantity: 10,
 *   availableIn: 2 // weeks
 * }, ... ]
 */
class ProductInventory {
  constructor() {
    let arrayOfInventory = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : [];
    this._data = {};
    if (arrayOfInventory) {
      arrayOfInventory.forEach(item => {
        const inventoryItem = new ProductInventory$1(item);
        this._data[inventoryItem.sku] = inventoryItem;
      });
    }
  }
  lookup(sku) {
    return this._data[sku];
  }
}

class BreadcrumbList {
  constructor() {
    let label = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : "Arc'teryx";
    let url = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : "/";
    // the following provide ONE spot for us to change the default URL pattern if necessary
    // of course object implementors can set this property if they choose to.
    this.categoryPrefix = "/c/";
    this.productPrefix = "/shop/";
    this._arr = [];
    this.add(label, url, null);
  }
  addCategory(label, slug) {
    return this.add(label, slug, "category");
  }
  add(label, slug) {
    let type = arguments.length > 2 && arguments[2] !== undefined ? arguments[2] : "product";
    if (label) {
      this._arr.push({
        label,
        slug,
        type
      });
    }
    return this;
  }
  toArray() {
    return this._arr.map(crumb => {
      const prefix = crumb.type === "category" ? this.categoryPrefix : crumb.type === "product" ? this.productPrefix : "";
      const label = crumb.label;
      const url = crumb.slug ? prefix + crumb.slug : "#";
      return {
        label,
        url
      };
    });
  }
  static $createForProduct(product) {
    let rootLabel = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : null;
    let rootUrl = arguments.length > 2 && arguments[2] !== undefined ? arguments[2] : "/";
    const breadcrumbs = new BreadcrumbList(rootLabel, rootUrl);
    if (!product.isUnisex) {
      breadcrumbs.addCategory(product.genderCategory, product.gender === "men" ? "mens" : "womens");
    }
    return breadcrumbs.add(product.name);
  }
  static $createForCategory(category) {
    let rootLabel = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : null;
    let rootUrl = arguments.length > 2 && arguments[2] !== undefined ? arguments[2] : "/";
    const breadcrumbs = new BreadcrumbList(rootLabel, rootUrl);
    const url = category.url.split("/").slice(1);
    const slug = [];
    url.forEach(u => {
      // Do not include sub-cat, use or collection in the breadrumbs
      if (!u.match("=") && u !== "collection" && u !== "col") {
        slug.push(u);
        breadcrumbs.add(u.replace(/-/g, " "), slug.join("/"), "category");
      } else if (u === "collection" || u === "col") {
        slug.push("collection");
      }
    });
    return breadcrumbs;
  }
}

class ProductCalloutLink extends Link {
  static $fields() {
    return _objectSpread2(_objectSpread2({}, super.$fields()), {}, {
      direction: this.$string(),
      colour: this.$string(),
      type: this.$string(),
      horizontalPosition: this.$number(),
      verticalPosition: this.$number(),
      analytics_label: this.$string()
    });
  }
}

// PLP's Category Intro
class CategoryIntro extends Model {
  static $fields() {
    return {
      handle: this.$attr(),
      title: this.$string(),
      textColour: this.$string(),
      backgroundPosition: this.$string(),
      backgroundImage: this.$string(),
      maxWidth: this.$string(),
      content: this.$string(),
      breadcrumbs: this.$array(),
      productCallout: this.$relateMany(ProductCalloutLink),
      productOutfit: this.$relateMany(Link),
      cta: this.$relateMany(Link)
    };
  }
}

class FilterFacet extends Model {
  static $fields() {
    return _objectSpread2(_objectSpread2({}, super.$fields()), {}, {
      id: this.$string(),
      label: this.$string(),
      urlParam: this.$string(),
      items: this.$attr(),
      hasChildren: this.$boolean(),
      child: this.$boolean()
    });
  }
}

class FacetItemCount extends Model {
  static $fields() {
    return _objectSpread2(_objectSpread2({}, super.$fields()), {}, {
      id: this.$string(),
      label: this.$string(),
      count: this.$number()
    });
  }
}

class FilterItem extends Model {
  static $fields() {
    return _objectSpread2(_objectSpread2({}, super.$fields()), {}, {
      id: this.$string(),
      label: this.$string(),
      urlParam: this.$string(),
      count: this.$number(),
      selected: this.$boolean(),
      toolTip: this.$string()
    });
  }
}

class CatalogFilterFacets extends Model {
  static $fields() {
    return _objectSpread2(_objectSpread2({}, super.$fields()), {}, {
      itemCount: this.$relateOne(FacetItemCount),
      filterCategories: this.$relateMany(FilterFacet),
      availability: this.$relateOne(FilterItem),
      sort: this.$relateOne(FilterFacet)
    });
  }
  static $_mapResultForBR() {
    return new CatalogFilterFacets(adaptFilterFacets(...arguments));
  }
  static $getFacetsForBR() {
    let facets = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : {};
    let configs = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : {};
    let itemCount = arguments.length > 2 && arguments[2] !== undefined ? arguments[2] : 0;
    const {
      market,
      country,
      gender,
      handle,
      parentCategories
    } = configs;
    let pageType = "main";
    let category = null;

    // Parent categories
    const isParentCategory = parentCategories === null || parentCategories === void 0 ? void 0 : parentCategories.some(parentCategory => category === parentCategory || (handle === null || handle === void 0 ? void 0 : handle.includes(parentCategory)));
    if (configs.collection) {
      pageType = "collection";
    } else if (configs.intendedUse) {
      pageType = "intendedUse";
    } else if (configs.category) {
      pageType = "category";
      if (configs.wid) {
        category = "wid";
      } else if (isParentCategory) {
        category = "parentCategory";
      } else {
        category = configs.category;
      }
    } else if (configs.subCategory) {
      pageType = "subCategory";
    }
    return this.$_mapResultForBR(facets, pageType, itemCount, market, country, gender, category);
  }
}

class Store extends Model {
  static $fields() {
    return {
      id: this.$string(),
      // SAP/Magento store_code
      name: this.$string(),
      url: this.$string(),
      latitude: this.$number(),
      longitude: this.$number(),
      phone: this.$string(),
      street: this.$string(),
      city: this.$string(),
      region: this.$string(),
      regionCode: this.$string(),
      postcode: this.$string(),
      country: this.$string(),
      distanceAway: this.$number()
    };
  }
}

const METERS = 1000;
const GEO_ACCURACY = 10;
const adaptStore = (data, location) => {
  let distanceAway = null;
  if (location) {
    distanceAway = getDistance(location, data, GEO_ACCURACY) / METERS;
  }
  return {
    id: data.pickup_location_code,
    name: data.name,
    url: data.description,
    latitude: data.latitude,
    longitude: data.longitude,
    email: data.email,
    phone: data.phone,
    street: data.street,
    city: data.city,
    regionCode: data.region_code,
    region: data.region,
    postcode: data.postcode,
    country: data.country_id,
    distanceAway
  };
};

class MagentoStore extends Store {
  static $create(store, geoLocation) {
    return new MagentoStore(adaptStore(store, geoLocation));
  }
}

// these are the facets ids given to us from the Bloomreach api call,
// they can be configured in the Bloomreach portal,
const DataPointParam = {
  collection: {
    key: "collection",
    label: "collection"
  },
  genders: {
    key: "genders",
    label: "gender"
  },
  categories: {
    key: "categories",
    label: "categories"
  },
  intended_use: {
    key: "intended_use",
    label: "intended use"
  },
  sub_categories: {
    key: "sub_categories",
    label: "sub categories"
  },
  materials: {
    key: "materials",
    label: "materials"
  },
  base_colours: {
    key: "base_colours",
    label: "base colour"
  },
  rating: {
    key: "rating",
    label: "rating"
  },
  size: {
    key: "size",
    label: "size"
  },
  size_men: {
    key: "size_men",
    label: "size"
  },
  size_women: {
    key: "size_women",
    label: "size"
  },
  activity: {
    key: "activity",
    label: "activity"
  }
};

// Size facet order
const SIZE_ORDER = ["XXS", "2XS", "XS", "S", "S-M", "M", "L", "L-XL", "XL", "XXL", "2XL", "2X", "XXXL", "3XL", "One Size", "SRT", "REG", "TALL", "NS", "00", "0", "2", "3.5", "4", "4.5", "5", "5.5", "6", "6.5", "7", "7.5", "8", "8.5", "9", "9.5", "10", "10.5", "11", "11.5", "12", "12.5", "13", "13.5", "14", "16", "28", "29", "30", "31", "32", "33", "34", "35", "36", "36 2/3", "36 &#8532;", "36 ⅔", "37", "37 1/3", "37 &#8531;", "37 ⅓", "38", "38 2/3", "38 &#8532;", "38 ⅔", "39 1/3", "39 &#8531;", "39 ⅓", "40", "40 2/3", "40 &#8532;", "40 ⅔", "41 1/3", "41 &#8531;", "41 ⅓", "42", "42 2/3", "42 &#8532;", "42 ⅔", "43 1/3", "43 &#8531;", "43 ⅓", "44", "44 2/3", "44 &#8532;", "44 ⅔", "45 1/3", "45 &#8531;", "45 ⅓", "46", "46 2/3", "46 &#8532;", "46 ⅔", "47 1/3", "47 &#8531;", "47 ⅓", "48", "48 2/3", "48 &#8532;", "48 ⅔", "49 1/3", "49 &#8531;", "49 ⅓"];

/*
Checks if object is empty
@param {Object} object
@return {Boolean} true if object is empty
*/
const isObjectEmpty = object => object && Object.keys(object).length === 0 && object.constructor === Object;
const getNormalizedFilterTabLabel = string => {
  var _string$replace, _string$replace$repla;
  const label = string === null || string === void 0 ? void 0 : (_string$replace = string.replace(/_/g, "")) === null || _string$replace === void 0 ? void 0 : (_string$replace$repla = _string$replace.replace(/\s/g, "")) === null || _string$replace$repla === void 0 ? void 0 : _string$replace$repla.toUpperCase();
  const DataPointParam = {
    SIZE: "FB_SIZE",
    BASECOLOUR: "FB_BASECOLOR",
    MATERIALS: "FB_MATERIALS",
    CATEGORIES: "FB_CATEGORIES",
    SUBCATEGORIES: "FB_SUBCATEGORIES",
    COLLECTION: "FB_COLLECTION",
    INTENDEDUSE: "INTENDED_USE",
    GENDER: "FB_GENDER",
    RATING: "FB_RATING",
    ACTIVITY: "ACTIVITY"
  };
  return DataPointParam[label];
};
const convertToBoolean = str => typeof str === "string" && str.toLowerCase().trim() === "true";
const getSortItems = market => {
  const items = [{
    id: "title-ascending",
    label: "Name (A→Z)"
  }, {
    id: "title-descending",
    label: "Name (Z→A)"
  }, {
    id: "price-ascending",
    label: "Price (Low→High)"
  }, {
    id: "price-descending",
    label: "Price (High→Low)"
  }, {
    id: "weight-ascending",
    label: "Weight (Low→High)"
  }];
  if (market === "outdoor") {
    items.push({
      id: "is_new-descending",
      label: "What's New"
    }, {
      id: "rating-descending",
      label: "Top Rated"
    });
  }
  if (market === "leaf") {
    items.push({
      id: "is_new-descending",
      label: "What's New"
    });
  }
  return items;
};

// convert size order array to map
const sizeOrderMap = SIZE_ORDER.reduce((acc, current, currentIdx) => _objectSpread2(_objectSpread2({}, acc), {}, {
  [current.toLowerCase()]: currentIdx
}), {});
const compareSize = (a, b) => {
  var _a$label, _b$label;
  const aIdx = a === null || a === void 0 ? void 0 : (_a$label = a.label) === null || _a$label === void 0 ? void 0 : _a$label.toLowerCase();
  const bIdx = b === null || b === void 0 ? void 0 : (_b$label = b.label) === null || _b$label === void 0 ? void 0 : _b$label.toLowerCase();
  if (typeof sizeOrderMap[aIdx] === "undefined") {
    return 1;
  } else if (typeof sizeOrderMap[bIdx] === "undefined") {
    return -1;
  }
  return sizeOrderMap[aIdx] - sizeOrderMap[bIdx];
};

const adaptProductTile$1 = function (data) {
  var _data, _data2, _data3, _colourOptions$;
  let country = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : "ca";
  let market = arguments.length > 2 && arguments[2] !== undefined ? arguments[2] : "outdoor";
  let selectedColours = arguments.length > 3 && arguments[3] !== undefined ? arguments[3] : [];
  const {
    pid: id,
    gender,
    slug,
    title: name,
    description: shortDescription,
    is_new: isNew = false,
    is_revised: isRevised = false,
    is_pro: isPro = false,
    collection = "",
    rating,
    review_count: reviewCount = 0,
    hover_image,
    analytics_name: analyticsName = "",
    colour_images_map: colourImagesMap = []
  } = data;
  let selectedColourOption = null;
  const getColourImagesMap = (_data = data === null || data === void 0 ? void 0 : data[`colour_images_map_${country}`]) !== null && _data !== void 0 ? _data : colourImagesMap;
  const colourOptions = getColourImagesMap.map(colourImageString => {
    const [baseColour, colour, selected, imageUrl, thumbImageUrl, colourBadges] = colourImageString === null || colourImageString === void 0 ? void 0 : colourImageString.split(":::");
    const badges = (colourBadges === null || colourBadges === void 0 ? void 0 : colourBadges.split(",").filter(value => value !== "null").map(badge => {
      const [code, label] = badge.split("--");
      return {
        code,
        label
      };
    })) || [];
    if (!selectedColourOption && selected === "true") {
      selectedColourOption = colour;
    }
    return {
      badges,
      label: colour,
      value: colour,
      image: _objectSpread2({
        label: `${name} ${colour}`
      }, adaptImageUrl(imageUrl)),
      thumbnail: _objectSpread2({
        label: `${name} ${colour}`
      }, adaptImageUrl(thumbImageUrl)),
      primaryColour: baseColour
    };
  });

  // Show the last selected colour for hero image
  if (selectedColours !== null && selectedColours !== void 0 && selectedColours.length) {
    // // Iterate backwards through the selectedColours array
    for (let i = selectedColours.length - 1; i >= 0; i--) {
      const lastSelectedColour = selectedColours[i];
      const colourMatch = colourOptions === null || colourOptions === void 0 ? void 0 : colourOptions.find(_ref => {
        let {
          label,
          primaryColour
        } = _ref;
        return label === lastSelectedColour || primaryColour === lastSelectedColour;
      });
      // Check if the last selected colour matches either colour or primaryColour
      if (colourMatch) {
        selectedColourOption = (colourMatch === null || colourMatch === void 0 ? void 0 : colourMatch.label) || null;
        break; // Exit the loop if a match is found
      }
    }
  }

  return {
    id,
    slug,
    name,
    analyticsName,
    gender,
    currencyCode: getCurrencyCode$1(country === null || country === void 0 ? void 0 : country.toLowerCase(), true),
    price: String((_data2 = data[`price_${country === null || country === void 0 ? void 0 : country.toLowerCase()}`]) !== null && _data2 !== void 0 ? _data2 : ""),
    discountPrice: String((_data3 = data[`discount_price_${country === null || country === void 0 ? void 0 : country.toLowerCase()}`]) !== null && _data3 !== void 0 ? _data3 : ""),
    minPrice: "",
    minDiscountPrice: null,
    colourOptions: {
      selected: selectedColourOption || ((_colourOptions$ = colourOptions[0]) === null || _colourOptions$ === void 0 ? void 0 : _colourOptions$.value) || "Black",
      // fallback select the first colour or select black
      options: colourOptions
    },
    sizeOptions: {
      // Not used
      selected: null,
      options: []
    },
    variants: [],
    // Not used

    // The following defined in CatalogProduct model
    // We will use the mainImage to represent the altImage (hover image). ProductTileComponent expects this.
    mainImage: _objectSpread2({
      label: name
    }, adaptImageUrl(hover_image)),
    market,
    marketingName: name,
    shortDescription,
    isNew: convertToBoolean(isNew),
    isRevised: convertToBoolean(isRevised),
    isPro: convertToBoolean(isPro),
    collection,
    description: null,
    // Not used

    // The following defined in ProductTile model
    rating,
    reviewCount
  };
};

const parseFacetIntoParentChild = _ref => {
  let {
    key,
    facetItems = []
  } = _ref;
  var _a;
  // Create object with parentId as key to store child items
  // This is to add children to parents using the object key instead of having a nested loop to find the parent
  const facetObj = {};
  if (!(facetItems === null || facetItems === void 0 ? void 0 : facetItems.length)) {
    return null;
  }
  // Add items to facetObj using parentId as key
  for (const {
    name,
    count
  } of facetItems) {
    // Don't include facets without translated labels
    if (!(name === null || name === void 0 ? void 0 : name.includes(":::"))) {
      continue;
    }
    const [ids, labels] = name.split(":::");
    const [parentId, childId] = ids.split(">");
    const [parentLabel, childLabel] = labels.split(">");
    // If no parent in facetObj, create it
    if (!(parentId in facetObj)) {
      facetObj[parentId] = {
        label: parentLabel,
        count: 0,
        items: []
      };
    }
    // Aggregate child count to created parent count, which doesn't exist in bloomreach
    facetObj[parentId].count += count;
    // Append child item to parent items
    facetObj[parentId].items.push({
      id: `${parentId}_${childId}`,
      label: childLabel,
      count
    });
  }
  const parsedParentItems = Object.entries(facetObj).map(_ref2 => {
    let [id, {
      label,
      count,
      items
    }] = _ref2;
    return {
      id,
      label,
      count,
      child: true,
      hasChildren: true,
      items
    };
  });
  return {
    id: key,
    label: getNormalizedFilterTabLabel((_a = DataPointParam[key]) === null || _a === void 0 ? void 0 : _a.label),
    hasParentChildFacets: true,
    hasChildren: true,
    child: true,
    multiSelect: true,
    items: parsedParentItems
  };
};

const adaptFacet = (facet, key, presentationKey, hasChildren, isChild) => {
  const facetLabel = getNormalizedFilterTabLabel(presentationKey);
  const multiSelect = key !== "rating"; // rating is set to single select

  const items = facet.map(_ref => {
    let {
      name,
      count,
      start
    } = _ref;
    let formattedLabel;
    let id = name;
    if (["size", "size_men", "size_women", "materials", "sub_categories"].includes(key)) {
      formattedLabel = name;
    } else if (key === "rating") {
      // Rating is a range and we use the start number value as the label
      // which are: *, 1, 2, 3, 4, 5
      let formattedStart = +start;
      // We want * to become 0 to reflect the correct start value
      if (start === "*" || !start) {
        formattedStart = 0;
      }
      formattedLabel = formattedStart;
      id = `${formattedStart}`;
    } else {
      const nameLower = name.toLowerCase();
      const label = nameLower === "men" || nameLower === "women" ? `${name}s` : name;
      formattedLabel = capitalize(label);
    }
    return {
      id,
      label: formattedLabel,
      count: count || 0
    };
  }) || [];
  return {
    id: key,
    label: facetLabel,
    hasChildren,
    child: isChild,
    multiSelect,
    items
  };
};
const validatedFacets = (facetItems, facetsBR) => {
  const facetsList = [];
  facetItems.forEach(_ref2 => {
    let {
      key
    } = _ref2;
    const facet = facetsBR[key];
    if (facet) {
      var _DataPointParam$key;
      const result = key === "activity" ? parseFacetIntoParentChild({
        key,
        facetItems: facet
      }) : adaptFacet(facet, key, ((_DataPointParam$key = DataPointParam[key]) === null || _DataPointParam$key === void 0 ? void 0 : _DataPointParam$key.label) || key, false, true);
      facetsList.push(result);
    } else {
      // Skip the current iteration
      return;
    }
  });
  return facetsList;
};
const getFacets = (pageType, category, gender, market, country, facetsBRObj) => {
  const {
    categories,
    collection,
    intended_use,
    // TODO: remove intended_use from the facets list
    sub_categories,
    materials,
    base_colours,
    rating,
    size,
    size_men,
    activity
  } = DataPointParam;

  // For US & CA, return size_gender facet when gender is specified
  const isNA = country === "us" || country === "ca";
  const sizeByGender = `size_${(gender === null || gender === void 0 ? void 0 : gender.slice(-1)) === "s" ? gender === null || gender === void 0 ? void 0 : gender.slice(0, -1) : gender}`;
  let formattedSize = gender && isNA ? DataPointParam[sizeByGender] : size;
  const setFacets = facetsList => validatedFacets(facetsList, facetsBRObj);
  switch (pageType) {
    case "collection":
      return setFacets([categories, materials, base_colours, rating, formattedSize]);
    // TODO: remove intended_use from the facets list
    case "intendedUse":
      return setFacets([collection, categories, materials, base_colours, rating, formattedSize]);
    case "category":
      // use size men's for accessories if no gender in url
      if (category === "accessories" && !gender) {
        formattedSize = size_men;
      }
      // wid are Dynamic PLPs and Parent Category PLPs have the same facets
      if (category === "wid" || category === "parentCategory") {
        return setFacets([categories, sub_categories, base_colours, rating, formattedSize]);
      } else {
        // TODO: remove intended_use from the facets list
        return setFacets([collection, activity, intended_use, sub_categories, base_colours, rating, formattedSize]);
      }
    case "subCategory":
      return setFacets([categories]);
    default:
      if (market === "outdoor") {
        // TODO: remove intended_use from the facets list
        return setFacets([collection, categories, activity, intended_use]);
      } else {
        return setFacets([collection, materials, base_colours]);
      }
  }
};

/**
  Adapt filter facets object returned from Bloomreach search API to the format that the UI expects.
  @param {Object} facets - facet_fields object returned from Bloomreach search API, e.g. {..., collection: []}
  @param {String} pageType - The page type of the current page.
  @param {Number} itemCount - The number of items returned from the search API.
  @param {String} market - The market of the current page.
  @param {String} country - The country of the current page.
  @param {String} gender - The specified gender of the current page e.g. men | women.
  @param {String} category - The category of the current page.
  @return {Object} - adapted filter facets object
*/
const adaptFilterFacets = (facets, pageType, itemCount, market, country, gender, category) => {
  const defaultStructure = {
    itemCount: {
      id: "items",
      label: "Items",
      count: itemCount
    },
    filterCategories: [{
      id: "refine",
      label: "Refine",
      hasChildren: true,
      items: []
    }],
    sort: {
      id: "Sort",
      label: "Sort",
      urlParam: "sort",
      items: getSortItems(market)
    }
  };
  if (isObjectEmpty(facets)) {
    console.log("no facets available");
    return defaultStructure;
  }
  const facetsList = getFacets(pageType, category, gender, market, country, facets);

  // sort size facet items
  const sizeFilterIdx = facetsList.findIndex(item => item && (item.id === DataPointParam.size.key || item.id === DataPointParam.size_men.key || item.id === DataPointParam.size_women.key));
  if (sizeFilterIdx > -1) {
    facetsList[sizeFilterIdx].items.sort(compareSize);
  }
  return _objectSpread2(_objectSpread2({}, defaultStructure), {}, {
    filterCategories: [{
      id: "refine",
      label: "Refine",
      hasChildren: true,
      items: facetsList
    }]
  });
};

/**
 * BloomreachProductTiles.$load()
 * @param {class} provider
 * @param {object} facetConfigs { market, country, gender, collection, intendedUse, category, subCategory }
 * @return {object} { productTiles = [], filterFacets = {} }
 */
class BloomreachProductTiles extends ProductTile {
  static async $load(_ref) {
    var _brData$response, _brData$response2, _brData$facet_counts, _brData$facet_counts2, _brData$facet, _brData$facet2, _brData$metadata;
    let {
      provider,
      facetConfigs = false,
      selectedColours
    } = _ref;
    const {
      market,
      country
    } = provider;
    const brData = await provider.fetchProducts();

    /** Product Tiles */
    const products = brData === null || brData === void 0 ? void 0 : (_brData$response = brData.response) === null || _brData$response === void 0 ? void 0 : _brData$response.docs;
    const productTiles = (products === null || products === void 0 ? void 0 : products.map(product => {
      const adaptedProductTile = adaptProductTile$1(product, country, market, selectedColours);
      return new ProductTile(adaptedProductTile);
    })) || [];

    /** Filter Facets */
    const itemCount = (brData === null || brData === void 0 ? void 0 : (_brData$response2 = brData.response) === null || _brData$response2 === void 0 ? void 0 : _brData$response2.numFound) || 0;
    const facetFields = (brData === null || brData === void 0 ? void 0 : (_brData$facet_counts = brData.facet_counts) === null || _brData$facet_counts === void 0 ? void 0 : _brData$facet_counts.facet_fields) || {};
    const facetRanges = (brData === null || brData === void 0 ? void 0 : (_brData$facet_counts2 = brData.facet_counts) === null || _brData$facet_counts2 === void 0 ? void 0 : _brData$facet_counts2.facet_ranges) || {};
    const facetFieldsWidget = (brData === null || brData === void 0 ? void 0 : (_brData$facet = brData.facet) === null || _brData$facet === void 0 ? void 0 : _brData$facet.fields) || [];
    const facetRangesWidget = (brData === null || brData === void 0 ? void 0 : (_brData$facet2 = brData.facet) === null || _brData$facet2 === void 0 ? void 0 : _brData$facet2.ranges) || [];

    /**
     * Filtering with the Pathways API widgets:
     * the facetFieldsWidget and faceRangesWidget will be arrays of objects
     * with this structure: [{key, value}, {key, value}]
     * format so it matches the desired structure: {key: value, key: value}
     */
    if (facetFieldsWidget !== null && facetFieldsWidget !== void 0 && facetFieldsWidget.length) {
      facetFieldsWidget === null || facetFieldsWidget === void 0 ? void 0 : facetFieldsWidget.forEach(_ref2 => {
        let {
          key,
          value
        } = _ref2;
        return facetFields[key] = value;
      });
    }
    if (facetRangesWidget !== null && facetRangesWidget !== void 0 && facetRangesWidget.length) {
      facetRangesWidget === null || facetRangesWidget === void 0 ? void 0 : facetRangesWidget.forEach(_ref3 => {
        let {
          key,
          value
        } = _ref3;
        return facetRanges[key] = value;
      });
    }

    /**
     * facetRanges keys will overwrite the facetFields keys if there are duplicates
     * which is the behaviour we want for ratings
     */
    const facets = _objectSpread2(_objectSpread2({}, facetFields), facetRanges);
    const filterFacets = facetConfigs ? CatalogFilterFacets.$getFacetsForBR(facets, facetConfigs, itemCount) : {};

    /**
     * Widget Data
     * For Cross Sells and Dynamic PLPs
     * structure: {id, name, description, type, rid}
     */
    const widgetData = (brData === null || brData === void 0 ? void 0 : (_brData$metadata = brData.metadata) === null || _brData$metadata === void 0 ? void 0 : _brData$metadata.widget) || {};
    return {
      productTiles,
      filterFacets,
      widgetData
    };
  }
}

class OrderHistoryEvent extends Model {
  static $fields() {
    return {
      date: this.$string(),
      status: this.$string()
    };
  }
}

class OrderHistoryTax extends Model {
  static $fields() {
    return {
      label: this.$string(),
      code: this.$string(),
      // Deprecated in AmerAdapter
      rate: this.$number(),
      amount: this.$number(),
      included: this.$boolean(),
      // Deprecated in AmerAdapter
      currency: this.$string()
    };
  }
}

class OrderHistoryShipment extends Model {
  static $fields() {
    return {
      trackingUrl: this.$string(),
      trackingNumber: this.$string()
    };
  }
}

class OrderHistory extends Model {
  static $fields() {
    return {
      orderNo: this.$string(),
      orderDate: this.$string(),
      status: this.$string(),
      trackingUrl: this.$string(),
      // Deprecated in AmerAdapter
      trackingNumber: this.$string(),
      // Deprecated in AmerAdapter
      currency: this.$string(),
      currencyCode: this.$string(),
      taxPercentage: this.$number(),
      subTotal: this.$number(),
      subtotalPrice: this.$number(),
      subtotalPriceUSD: this.$number(),
      subtotalPriceEUR: this.$number(),
      subtotalPriceCAD: this.$number(),
      taxTotal: this.$number(),
      tax: this.$number(),
      total: this.$number(),
      totalPrice: this.$number(),
      totalSavings: this.$number(),
      shipments: this.$relateMany(OrderHistoryShipment),
      shippingFee: this.$number(),
      taxes: this.$relateMany(OrderHistoryTax),
      products: this.$relateMany(CartProduct),
      events: this.$relateMany(OrderHistoryEvent) // Deprecated in AmerAdapter
    };
  }
}

const isInstoreOrder = orderId => Boolean(orderId.charAt(0).match(/[a-z]/i));

/* eslint-disable no-magic-numbers */
const genderSlugMap = {
  MALE: "mens",
  FEMALE: "womens"
};
const genderMap = {
  MALE: "men",
  FEMALE: "women",
  NEUTRAL: "unisex"
};
const statusMap = {
  CANCELLED: "CANCELLED",
  NEW: "NEW",
  APPROVED: "APPROVED",
  ORDERCONFIRMED: "ORDER CONFIRMED",
  PARTIALAPPROVED: "PARTIAL APPROVED",
  COMPLETE: "SHIPPED",
  ERRORPAYMENT: "ERROR PAYMENT",
  ORDERRECEIVED: "ORDER RECEIVED",
  REJECTED: "ORDER REJECTED",
  INPROGRESS: "IN PROGRESS",
  PARTIALCANCELLED: "PARTIAL CANCELLED",
  FAILED: "FAILED"
};
const NAMset = new Set(["CAD", "USD"]);
const iS_NAM = currency => NAMset.has(currency);
function _adaptTaxLabel(taxLabel) {
  return taxLabel.indexOf("-") > 0 ? taxLabel.split("-")[0] : taxLabel;
}
function _adaptTaxes(data, currency) {
  return !Array.isArray(data) ? [] : data.map(_ref => {
    let {
      tax_amount: amount,
      tax_rate: rate,
      tax_name: taxLabel
    } = _ref;
    return {
      label: _adaptTaxLabel(taxLabel),
      rate,
      amount,
      currency
    };
  });
}
const _adaptTaxTotalPercentage = (taxArr, currency) => {
  let taxPercentTotal = 0;
  if (iS_NAM(currency)) {
    if (Array.isArray(taxArr) && taxArr !== null && taxArr !== void 0 && taxArr.length) {
      taxPercentTotal = taxArr.reduce((prev, cur) => prev + (cur === null || cur === void 0 ? void 0 : cur.tax_rate) || 0, 0);
    }
    taxPercentTotal = taxPercentTotal > 1 ? Math.round(taxPercentTotal) : (parseFloat(taxPercentTotal, 10) * 100).toFixed(2);
    return taxPercentTotal;
  }
  return 0;
};
const _adaptShipments = data => {
  return !Array.isArray(data) ? [] : data.map(_ref2 => {
    let {
      tracking_link: trackingUrl,
      tracking_number: trackingNumber
    } = _ref2;
    return {
      trackingUrl,
      trackingNumber
    };
  });
};
const _getSlugFromName = (gender, name) => {
  const slug = [];
  if (gender) {
    slug.push(gender);
  }
  slug.push(name.toLocaleLowerCase().replace(/ /g, "-").replace(/'/g, ""));
  return slug.join("/");
};
const _getAttributes = (arr, key) => {
  let attributeValue = "";
  if (arr) {
    arr.forEach(_ref3 => {
      let {
        name,
        value
      } = _ref3;
      if (name.toLowerCase() === key.toLowerCase()) {
        attributeValue = value;
      }
    });
  }
  return attributeValue;
};
const _checkDiscountPrice = _ref4 => {
  let {
    unit_price,
    currency,
    amount,
    quantity
  } = _ref4;
  let itemAmount = amount === null || amount === void 0 ? void 0 : amount.amount_tax_included; // EMEA
  if (iS_NAM(currency)) {
    itemAmount = amount === null || amount === void 0 ? void 0 : amount.amount_tax_excluded; // NA
  }

  if (unit_price > itemAmount / quantity) {
    return itemAmount / quantity;
  }
  return 0;
};

/**
 *
 *  This function will attempt to get the Color name from the image url
 *  Example; image_url = "https://images.arcteryx.com/F21/1350x1710/Sentinel-AR-Jacket-W-Magic-Wander.jpg"
 *
 */
const _getColorLabelFromUrl = _ref5 => {
  let {
    name,
    image_url
  } = _ref5;
  try {
    // Remove gender from name e.g. "Sentinel AR Jacket WOMENS"
    const productName = name.replace(/women's/i, "").replace(/womens/i, "").replace(/men's/i, "").replace(/mens/i, "").replace(/neutral/i, "").trim().replace(/ /g, "-");
    const image = new URL(image_url);
    const imagePath = image.pathname.split("/");
    const fileName = imagePath[imagePath.length - 1];
    return fileName.replace(`${productName}-`, "").replace(/W-/g, "").replace(/.jpg/g, "").replace(/.png/g, "").replace(/-/g, " ");
  } catch (_unused) {
    return null;
  }
};
const _getColorLabel = product => _getAttributes(product.attributes, "color_label") || _getColorLabelFromUrl(product) || "";
function _getUnitPrice(currency, unit_price, discountPrice, amount, quantity) {
  if (iS_NAM(currency)) {
    return unit_price;
  }
  return discountPrice ? unit_price : (amount === null || amount === void 0 ? void 0 : amount.amount_tax_included) / quantity;
}
const _adaptProducts = (data, currency) => {
  return !Array.isArray(data) ? [] : data.map(_ref6 => {
    let {
      quantity,
      unit_price,
      product,
      amount
    } = _ref6;
    const discountPrice = _checkDiscountPrice({
      unit_price,
      currency,
      amount,
      quantity
    });
    const unitPrice = _getUnitPrice(currency, unit_price, discountPrice, amount, quantity);
    const gender = genderMap[_getAttributes(product.attributes, "gender")] || "unisex";
    const genderSlug = genderSlugMap[_getAttributes(product.attributes, "gender")] || undefined;
    const slug = _getSlugFromName(genderSlug, product.name);
    const selectedSize = _getAttributes(product.attributes, "Size") || _getAttributes(product.attributes, "size");
    return {
      id: product.sku,
      name: product.name,
      gender,
      slug,
      analyticsName: product.name,
      quantity,
      currencyCode: currency,
      price: unitPrice,
      discountPrice,
      colourOptions: {
        selected: _getColorLabel(product),
        options: [{
          hexCode: _getAttributes(product.attributes, "hex_value").replace("#", ""),
          label: _getColorLabel(product),
          value: _getColorLabel(product),
          image: {
            label: `${product.name} ${_getColorLabel(product)}`,
            url: product.image_url
          }
        }]
      },
      selectedSize: {
        label: selectedSize
      },
      sizeOptions: {
        selected: selectedSize,
        options: [{
          label: selectedSize,
          value: selectedSize
        }]
      }
    };
  });
};
const _exchangeRate = (currency, exchangeCurrency, exchangeRateLookup) => {
  var _exchangeRateLookup$c;
  return (exchangeRateLookup === null || exchangeRateLookup === void 0 ? void 0 : (_exchangeRateLookup$c = exchangeRateLookup[currency]) === null || _exchangeRateLookup$c === void 0 ? void 0 : _exchangeRateLookup$c[exchangeCurrency]) || 1;
};
const _adaptSubTotal = function (data, currency, exchangeCurrency) {
  var _data$amount, _data$amount2;
  let exchangeRateLookup = arguments.length > 3 && arguments[3] !== undefined ? arguments[3] : {};
  const exchangeRate = _exchangeRate(currency, exchangeCurrency, exchangeRateLookup);
  return iS_NAM(data.currency) ? (data === null || data === void 0 ? void 0 : data.item_total) * exchangeRate || (data === null || data === void 0 ? void 0 : (_data$amount = data.amount) === null || _data$amount === void 0 ? void 0 : _data$amount.amount_tax_excluded) * exchangeRate : (data === null || data === void 0 ? void 0 : (_data$amount2 = data.amount) === null || _data$amount2 === void 0 ? void 0 : _data$amount2.amount_tax_included) * exchangeRate;
};
const adaptAmerOrderHistory = function (data) {
  var _data$amount3, _data$amount4, _data$amount5, _data$amount6, _data$amount7, _data$amount8;
  let exchangeRateLookup = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : {};
  if (isInstoreOrder(data.original_order_id)) {
    return null; // Exclude in-store purchases
  }

  return {
    orderNo: data.original_order_id,
    orderDate: data.creation_date,
    status: statusMap[data.status] || data.status,
    currencyCode: data.currency,
    subtotalPrice: iS_NAM(data.currency) ? (data === null || data === void 0 ? void 0 : data.item_total) || (data === null || data === void 0 ? void 0 : (_data$amount3 = data.amount) === null || _data$amount3 === void 0 ? void 0 : _data$amount3.amount_tax_excluded) : data === null || data === void 0 ? void 0 : (_data$amount4 = data.amount) === null || _data$amount4 === void 0 ? void 0 : _data$amount4.amount_tax_included,
    subtotalPriceCAD: _adaptSubTotal(data, data.currency, "CAD", exchangeRateLookup),
    subtotalPriceUSD: _adaptSubTotal(data, data.currency, "USD", exchangeRateLookup),
    subtotalPriceEUR: _adaptSubTotal(data, data.currency, "EUR", exchangeRateLookup),
    tax: data === null || data === void 0 ? void 0 : (_data$amount5 = data.amount) === null || _data$amount5 === void 0 ? void 0 : _data$amount5.tax_amount,
    taxPercentage: _adaptTaxTotalPercentage(data === null || data === void 0 ? void 0 : (_data$amount6 = data.amount) === null || _data$amount6 === void 0 ? void 0 : _data$amount6.taxes, data.currency),
    totalPrice: data === null || data === void 0 ? void 0 : (_data$amount7 = data.amount) === null || _data$amount7 === void 0 ? void 0 : _data$amount7.amount_tax_included,
    totalSavings: data.discount_amount,
    shipments: _adaptShipments(data === null || data === void 0 ? void 0 : data.shipments),
    shippingFee: data.shipping_fee_tax_excluded,
    taxes: _adaptTaxes(data === null || data === void 0 ? void 0 : (_data$amount8 = data.amount) === null || _data$amount8 === void 0 ? void 0 : _data$amount8.taxes, data.currency),
    products: _adaptProducts(data === null || data === void 0 ? void 0 : data.items, data === null || data === void 0 ? void 0 : data.currency)
  };
};

var currencyCodeMap = {
  at: "EUR",
  be: "EUR",
  ca: "CAD",
  ch: "CHF",
  cn: "CNY",
  cz: "EUR",
  de: "EUR",
  dk: "DKK",
  es: "EUR",
  fi: "EUR",
  fr: "EUR",
  gb: "GBP",
  jp: "JPY",
  ie: "EUR",
  it: "EUR",
  nl: "EUR",
  no: "NOK",
  pl: "PLN",
  se: "SEK",
  us: "USD"
};

const currencyMapArr = ["USD", "CAD", "EUR"];
const getCurrencyCode = country => {
  return currencyCodeMap[country] !== undefined ? currencyCodeMap[country] : "CAD";
};
const getPurchaseLimitsGroupTypeValue = (userType, purchaseLimitsLookup, country) => {
  const currencyCode = getCurrencyCode(country);
  return purchaseLimitsLookup[userType] !== undefined ? purchaseLimitsLookup[userType][currencyCode] : null;
};
const getPurchaseLimitsGroupType = (userType, purchaseLimitsLookup) => {
  return purchaseLimitsLookup[userType] !== undefined ? purchaseLimitsLookup[userType] : null;
};
const getTotalPriceFromOrders = (orders, code) => {
  return (orders === null || orders === void 0 ? void 0 : orders.reduce((acc, cur) => acc + cur[`subtotalPrice${code}`], 0)) || 0;
};

class AmerOrderHistory extends OrderHistory {
  static $create(data, exchangeRateLookup) {
    return new OrderHistory(adaptAmerOrderHistory(data, exchangeRateLookup));
  }

  /**
   *   Fetches order history from Amer order history service
   *   @param  {string}  env - The environment to use (dev, stage, prod)
   *   @param  {string}  email - The email address associated to the order
   *   @param  {string}  orderNumber - The orderNumber of the order
   *   @param {object}  exchangeRateLookup - The object for the exchange rate
   */
  static async $getSingleOrder(env, email, orderNumber, exchangeRateLookup) {
    const provider = new AmerOrderHistoryProvider(env);
    const resp = await provider.getSingleOrder(email, orderNumber);
    if (Object.keys(resp).length) {
      const order = this.$create(resp.data, exchangeRateLookup);
      return order.orderNo ? order : null;
    }
    return null;
  }

  /**
   *   Fetches order history from Amer order histroy service
   *   @param {string}  env - The environment to use (dev, stage, prod)
   *   @param {string}  jwt - The JWT token for an authenticated user
   *   @param {number}  monthFilter - The number of month to filter orders by. Valid values are 6 or 12.
   *   @param {string}  yearFilter - The current year valid from January 1st. Valid value is current_year.
   *   @param {object}  exchangeRateLookup - The object for the exchange rate
   */
  static async $getMultipleOrders(env, jwt, monthFilter, yearFilter, exchangeRateLookup) {
    const provider = new AmerOrderHistoryProvider(env);
    const resp = await provider.getMultipleOrders(jwt, monthFilter, yearFilter);
    if (Object.keys(resp).length) {
      // filter out null orders and orders from In-store
      const orders = resp.data.filter(order => order !== null && !isInstoreOrder(order.original_order_id));
      return orders.map(data => this.$create(data, exchangeRateLookup));
    }
    return null;
  }

  /**
   *   @param {string} country - The country the user visiting
   *   @param {string} userType - The userType (AR, AM, IN, PR, LX)
   *   @param {object} purchaseLimitsLookup Object for purchase limit based on group type in different currency
   *   @param {object} orders Object for orders placed since January 1st, each year
   */
  static $isPurchaseLimitReached(country, userType, purchaseLimitsLookup, orders) {
    const purchaseLimit = getPurchaseLimitsGroupTypeValue(userType, purchaseLimitsLookup, country);
    const currencyCode = getCurrencyCode(country);
    const sumOfSubTotals = getTotalPriceFromOrders(orders, currencyCode);
    return sumOfSubTotals >= purchaseLimit;
  }

  /**
   *   @param {string} userType - The userType (AR, AM, IN, PR, LX)
   *   @param {object} purchaseLimitsLookup The object for purchase limit based on group type in different currency
   *   @param {object} orders The object for order history from Jan 1st.
   */
  static $remainingBalance(userType, purchaseLimitsLookup, orders) {
    const purchaseLimitType = getPurchaseLimitsGroupType(userType, purchaseLimitsLookup) || {};
    return currencyMapArr.map(currency => {
      const totals = getTotalPriceFromOrders(orders, currency);
      let remainingBalance = purchaseLimitType[currency] - totals;
      remainingBalance = remainingBalance > 0 ? remainingBalance : 0;
      return `${currency}:${remainingBalance}`;
    });
  }
}

class User extends Model {
  static $fields() {
    return {
      id: this.$string(),
      uuid: this.$string(),
      userName: this.$string(),
      firstName: this.$string(),
      lastName: this.$string(),
      groupId: this.$string(),
      groupPrefix: this.$string(),
      workdayId: this.$number(),
      email: this.$string(),
      language: this.$string(),
      phoneNumber: this.$string(),
      country: this.$string(),
      postalCode: this.$string(),
      market: this.$string(),
      disableDiscount: this.$boolean(),
      emailSubscriptionSource: this.$string(),
      globalMarketingConsent: this.$boolean(),
      consent: this.$attr(),
      shippingAddress: this.$relateOne(Address),
      billingAddress: this.$relateOne(Address),
      remainingBalance: this.$array()
    };
  }
}

const _booleanValue = value => parseInt(value, 0) === 1;
const _isUndefined = value => value === "undefined" ? null : value;
const userMappingToForgeRock = {
  id: "_id",
  uuid: "frIndexedString2",
  userName: "userName",
  firstName: "givenName",
  lastName: "sn",
  groupId: "frUnindexedString1",
  workdayId: "frIndexedInteger1",
  email: "mail",
  language: "frUnindexedString2",
  phoneNumber: "telephoneNumber",
  country: "country",
  postalCode: "postalCode",
  globalMarketingConsent: "frUnindexedInteger2",
  consentMarketingOutdoor: "preferences.outdoorMarketingEmail",
  consentMarketingOutlet: "preferences.outletMarketingEmail",
  consentMarketingVeilance: "preferences.veilanceMarketingEmail",
  consentMarketingUsedGear: "preferences.usedGearEmail",
  consentMarketingAcademies: "preferences.academiesEmail",
  consentMarketingPro: "preferences.proMarketingEmail",
  consentMarketingIndustry: "preferences.indMarketingEmail",
  shippingAddress1: "postalAddress",
  shippingAddress2: "frUnindexedString3",
  shippingCity: "city",
  shippingRegion: "stateProvince",
  shippingPostalCode: "postalCode",
  shippingCountry: "country",
  shippingPoBox: "frUnindexedInteger5",
  billingAddress1: "frUnindexedString4",
  billingAddress2: "frUnindexedString5",
  billingCity: "frIndexedString1",
  billingRegion: "frIndexedString3",
  billingPostalCode: "frIndexedString4",
  billingCountry: "frIndexedString5",
  market: "description",
  disableDiscount: "frUnindexedInteger1",
  emailSubscriptionSource: "frUnindexedMultivalued1",
  remainingBalance: "frIndexedMultivalued1"
};
const adaptConsent = data => {
  const consentObj = {};
  const {
    preferences
  } = data;
  if (preferences) {
    Object.keys(preferences).forEach(key => {
      consentObj[key] = preferences[key];
    });
  }
  return consentObj;
};
const adaptShippingAddress = data => ({
  address1: data.postalAddress,
  address2: _isUndefined(data.frUnindexedString3),
  city: data.city,
  region: _isUndefined(data.stateProvince),
  postalCode: data.postalCode,
  country: data.country,
  poBox: _booleanValue(data.frUnindexedInteger5)
});
const adaptBillingAddress = data => ({
  address1: data.frUnindexedString4,
  address2: _isUndefined(data.frUnindexedString5),
  city: data.frIndexedString1,
  region: _isUndefined(data.frIndexedString3),
  postalCode: data.frIndexedString4,
  country: data.frIndexedString5,
  poBox: false
});
const adaptUser = data => {
  var _data$frUnindexedStri;
  return {
    id: data._id,
    uuid: data.frIndexedString2,
    userName: data.userName,
    firstName: data.givenName,
    lastName: data.sn,
    groupId: data.frUnindexedString1,
    groupPrefix: ((_data$frUnindexedStri = data.frUnindexedString1) === null || _data$frUnindexedStri === void 0 ? void 0 : _data$frUnindexedStri.split("-")[0]) || "",
    workdayId: data.frIndexedInteger1,
    email: data.mail,
    language: data.frUnindexedString2,
    phoneNumber: data.telephoneNumber,
    country: data.country,
    postalCode: data.postalCode,
    market: data.description,
    disableDiscount: data.frUnindexedInteger1 === 1,
    emailSubscriptionSource: Array.isArray(data.frUnindexedMultivalued1) && data.frUnindexedMultivalued1.length ? data.frUnindexedMultivalued1[0] : null,
    globalMarketingConsent: _booleanValue(data.frUnindexedInteger2),
    consent: adaptConsent(data),
    shippingAddress: adaptShippingAddress(data),
    billingAddress: adaptBillingAddress(data),
    remainingBalance: data === null || data === void 0 ? void 0 : data.frIndexedMultivalued1
  };
};
const updateFieldInForgeRock = function (field, value) {
  let operation = arguments.length > 2 && arguments[2] !== undefined ? arguments[2] : "replace";
  const temp = {
    operation,
    field: userMappingToForgeRock[field] || field,
    value
  };
  if (operation === "remove") {
    delete temp.value;
  }
  return temp;
};
const adaptFormData = (formData, operation) => {
  const adaptedData = [];
  if (Array.isArray(formData)) {
    formData.forEach(item => {
      adaptedData.push(updateFieldInForgeRock(item.field, item.value, operation));
    });
  }
  return adaptedData;
};

const FailedPolicyValidationMessage = "Failed policy validation";

/**
 * @param e {Error}
 * @returns {boolean}
 */
const isFailedPolicyValidationError = e => e.message === FailedPolicyValidationMessage;
class FailedPolicyValidationError extends Error {
  constructor(responseJson) {
    super(FailedPolicyValidationMessage);
    this.responseJson = responseJson;
  }
}

class ForgeRockUser extends User {
  // test lint fix
  static $create(data) {
    return new User(adaptUser(data));
  }

  /**
   *  Function to retrieve user profile from ForgeRock IDC and saves it to localStorage
   *  @param config {Object}  Configuration parameters
   *  @param {string}  config.baseUrl      (Required)  the base url to Forgerock AM
   *  @param {string}  config.realm        (Required)  the realm or realm path in which the authentication tree is configured
   *  @param {string}  config.accessToken  (Required)  the accessToken from an authenticated user
   *  @param {string}  config.id           (Required)  the ForgeRock id of the user
   */
  static async $load(config) {
    const userProfile = await getUserProfile(config);
    if (userProfile) {
      setForgeRockUser(adaptUser(userProfile));
      setSessionStorage(adaptUser(userProfile));
    }
    return getForgeRockUser();
  }

  /**
   *  Function to update a user profile in ForgeRock IDC
   *  @param config {Object}  Configuration parameters
   *  @param {string}  config.baseUrl      (Required)  the base url to Forgerock AM
   *  @param {string}  config.realm        (Required)  the realm or realm path in which the authentication tree is configured
   *  @param {string}  config.accessToken  (Required)  the accessToken from an authenticated user
   *  @param {string}  config.id           (Required)  the ForgeRock id of the user
   *  @param {object}  userData            (Required)  the array of form data objects to save user profile e.g. [{ field: "fieldName", value: "newValue"}]
   *  @param {string}  operation           (optional)  by default is 'replace' but we can pass as 'remove' as a value
   */
  static async $update(config, userData, operation) {
    const response = await saveUserProfile(config, adaptFormData(userData, operation));
    if (isFailedPolicyValidationError(response)) {
      throw new FailedPolicyValidationError(response);
    }
    setForgeRockUser(adaptUser(response));
    return response;
  }

  /**
   *  Function to change a user password in ForgeRock IDC
   *  @param config {Object}  Configuration parameters
   *  @param {string}  config.baseUrl      (Required)  the base url to Forgerock AM
   *  @param {string}  config.realm        (Required)  the realm or realm path in which the authentication tree is configured
   *  @param {string}  config.accessToken  (Required)  the accessToken from an authenticated user
   *  @param {string}  config.id           (Required)  the ForgeRock id of the user
   *  @param {string}  newPassword         (Required)  the new password
   */
  static async $changePassword(config, newPassword) {
    return await changeUserPassword(config, newPassword);
  }
  static $getProfile() {
    const userProfile = getForgeRockUser();
    if (userProfile) {
      return JSON.parse(userProfile);
    }
    return null;
  }

  /**
   *  @param {string}   redirectUrl (Required)  the URL to redirect after a user logs out
   */
  static async $logout(redirectUrl) {
    await logout(redirectUrl);
  }
}

const adaptOrderHistoryToCart = orderHistory => {
  const {
    currency,
    orderNo,
    subTotal,
    taxTotal,
    taxes,
    total,
    products
  } = orderHistory;
  const cartQty = products.reduce((acc, obj) => acc + (obj === null || obj === void 0 ? void 0 : obj.quantity), 0);
  const taxPercentage = taxes.reduce((acc, obj) => acc + (obj === null || obj === void 0 ? void 0 : obj.rate), 0);
  return {
    id: orderNo,
    currencyCode: currency,
    quantity: cartQty,
    tax: taxTotal,
    taxPercentage,
    products,
    subtotalPrice: subTotal,
    totalPrice: total
  };
};

const getCountryCode = currencyCode => {
  return currencyCode.split("_")[0];
};
const removeBaseUrlToGenerateSlug = url => {
  // Define the base URL to remove
  const baseUrl = "https://arcteryx.com/shop/";
  // Check if the URL starts with the base URL
  if (url.startsWith(baseUrl)) {
    // Remove the base URL from the URL
    return url.slice(baseUrl.length);
  }
  // Return the original URL if it doesn't start with the base URL
  return url;
};

/**

Returns the value of the default color for a given product.
@param {Object} props - An object containing the product's colors and default color code.
@returns {string|null} - The value of the default color or null if it cannot be found.
*/
const getDefaultColor = props => {
  return props.colors.find(color => color["@code"] === props.defaultColorCode);
};
const getDefaultColorValue = props => {
  const color = getDefaultColor(props);
  return color ? color.value : null;
};

/**
 * @typedef {Object} ColorOption
 * @property {string} value - The color value.
 * @property {string} code - The color code.
 */

/**
 * @typedef {Object} ProductData
 * @property {number} id - The product ID.
 * @property {string} name - The product name.
 * @property {Object.<string, string>} name_languages - The product name in different languages.
 * @property {string} currency - The currency code.
 * @property {number} price - The product price.
 * @property {ColorOption[]} colors - The available color options.
 * @property {string} defaultCode - The default color code.
 * @property {string[]} [images] - The product images.
 * @property {string} subname - The product subname.
 * @property {boolean} new - Whether the product is new.
 * @property {boolean} revised - Whether the product is revised.
 * @property {boolean} pro - Whether the product is a pro product.
 * @property {number} rating - The product rating.
 * @property {number} review_count - The number of reviews for the product.
 * @property {number} score - The product score.
 * @property {string} matchText - The product match text.
 * @property {string[]} matchPreferences - The product match preferences.
 */

/**
 * @param {ProductData} data - The product data.
 * @returns {{
 *  id: number,
 *  slug: string,
 *  name: string,
 *  analyticsName: string,
 *  currencyCode: string,
 *  price: number,
 *  colourOptions: {
 *    selected: string,
 *    options: {
 *      label: string,
 *      value: string,
 *      image: {
 *        label: null,
 *        pathname: null,
 *        url: null,
 *      },
 *      primaryColour: string,
 *    }[],
 *  },
 *  sizeOptions: {
 *    selected: null,
 *    options: any[],
 *  },
 *  variants: any[],
 *  mainImage: {
 *    label: string,
 *    pathname: string,
 *    url: string,
 *  } | null,
 *  marketingName: string,
 *  description: string,
 *  shortDescription: string,
 *  discountPrice: null,
 *  isNew: boolean,
 *  isRevised: boolean,
 *  isPro: boolean,
 *  rating: number,
 *  reviewCount: number,
 *  score: number,
 *  matchText: string,
 *  matchPreferences: string[],
 * }}
 */
const adaptCartfulProductTile = data => {
  const extractColourOptions = () => {
    const placeholderImg = "https://images.arcteryx.com/pdp/placeholder.jpg";
    const selected = getDefaultColorValue(data);
    const options = data.colors.map(_ref => {
      let {
        value: label,
        field,
        thumbnail
      } = _ref;
      const imageUrl = field[0].values[0] || placeholderImg;
      return {
        label,
        value: label,
        image: {
          label,
          url: imageUrl
        },
        thumbnail: {
          label,
          url: thumbnail
        },
        primaryColour: label
      };
    });
    return {
      selected,
      options
    };
  };
  const mainImage = (() => {
    const {
      name,
      alt_image_url: url
    } = data;
    return {
      label: `Arc'teryx ${name}`,
      pathname: url,
      url
    };
  })();
  return {
    id: data.id,
    slug: removeBaseUrlToGenerateSlug(data.full_url),
    name: data.name,
    analyticsName: data.name_languages.en,
    currencyCode: getCurrencyCode$1(getCountryCode(data.currency), true),
    price: data.price,
    colourOptions: extractColourOptions(),
    sizeOptions: {
      selected: null,
      options: []
    },
    variants: [],
    mainImage: mainImage,
    marketingName: data.name,
    description: data.subname,
    shortDescription: data.subname,
    discountPrice: null,
    isNew: data.new,
    isRevised: data.revised,
    isPro: data.pro,
    rating: data.rating,
    reviewCount: data.review_count,
    score: data.score,
    matchText: data.matchText,
    matchPreferences: data.matchPreferences
  };
};

const adaptProductTile = data => {
  const {
    altImageUrl,
    colors,
    collection,
    collectionLabel,
    currency: currencyCode,
    discountPrice,
    gender,
    isNew,
    isRevised,
    isPro,
    market,
    name,
    objectID: id,
    price,
    subMktgName: shortDescription,
    review_value: rating,
    review_count: reviewCount,
    url_name: slug,
    translatedNames
  } = data;
  const genderMap = {
    MALE: "men",
    FEMALE: "women"
  };
  const colourOptions = () => {
    var _colors$find, _colors$;
    const placeholderImg = "https://images.arcteryx.com/pdp/placeholder.jpg";
    const selected = (colors === null || colors === void 0 ? void 0 : (_colors$find = colors.find(_ref => {
      let {
        Selected
      } = _ref;
      return Selected;
    })) === null || _colors$find === void 0 ? void 0 : _colors$find.name) || ((_colors$ = colors[0]) === null || _colors$ === void 0 ? void 0 : _colors$.name) || "";
    const options = colors === null || colors === void 0 ? void 0 : colors.map(_ref2 => {
      let {
        badges,
        name: label,
        HexValue: hexCode,
        baseColor: primaryColour,
        imageUrl,
        thumbnailUrl
      } = _ref2;
      return {
        badges,
        label,
        value: label,
        image: {
          label: `${name} ${label}`,
          url: imageUrl || placeholderImg
        },
        thumbnail: {
          label: `${name} ${label}`,
          url: thumbnailUrl || imageUrl || placeholderImg
        },
        hexCode,
        primaryColour
      };
    });
    return {
      selected,
      options
    };
  };
  return {
    analyticsName: translatedNames !== null && translatedNames !== void 0 && translatedNames.length ? translatedNames[0] : name,
    currencyCode,
    colourOptions: colourOptions(),
    collection,
    collectionLabel,
    discountPrice,
    gender: genderMap[gender] || "unisex",
    id,
    isNew,
    isRevised,
    isPro,
    mainImage: {
      label: name,
      url: altImageUrl
    },
    minPrice: null,
    minDiscountPrice: null,
    market,
    marketingName: name,
    name,
    price,
    shortDescription,
    slug,
    rating,
    reviewCount,
    // Not used
    description: null,
    sizeOptions: {
      selected: null,
      options: []
    },
    variants: []
  };
};

const adaptSort = function (indexName) {
  let market = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : "outdoor";
  const items = [{
    id: "name-ascending",
    value: `${indexName}_name_asc`,
    label: "Name (A→Z)"
  }, {
    id: "name-descending",
    value: `${indexName}_name_desc`,
    label: "Name (Z→A)"
  }, {
    id: "weight",
    value: `${indexName}_weight_asc`,
    label: "Weight (Low→High)"
  }, {
    id: "price-ascending",
    value: `${indexName}_price_asc`,
    label: "Price (Low→High)"
  }, {
    id: "price-descending",
    value: `${indexName}_price_desc`,
    label: "Price (High→Low)"
  }];
  if ((market === null || market === void 0 ? void 0 : market.toLowerCase()) === "outdoor") {
    items.push({
      id: "new-descending",
      value: `${indexName}_new_desc`,
      label: "What's New"
    }, {
      id: "rating-descending",
      value: `${indexName}_rating_desc`,
      label: "Top Rated"
    });
  }
  return {
    id: "sort",
    label: "Sort",
    urlParam: "sort",
    items
  };
};

const adaptRefinementsApplied = _ref => {
  let {
    list,
    t,
    sortList = []
  } = _ref;
  if (!(list !== null && list !== void 0 && list.length)) {
    return [];
  }
  const preparedList = list.map(_ref2 => {
    let {
      id,
      items,
      value
    } = _ref2;
    if (id === "sortBy") {
      return items.map(item => {
        const filterSortByReplica = sortList.find(_ref3 => {
          let {
            value: replicaName
          } = _ref3;
          return replicaName === item.label;
        });
        const sortLabel = filterSortByReplica === null || filterSortByReplica === void 0 ? void 0 : filterSortByReplica.label;
        return _objectSpread2(_objectSpread2({}, item), {}, {
          id,
          type: "sort",
          label: t(sortLabel),
          value
        });
      });
    } else {
      return items.map(item => _objectSpread2(_objectSpread2({}, item), {}, {
        id,
        type: "filter"
      }));
    }
  }).map(obj => obj);
  const normalizedList = [].concat(...preparedList);
  return normalizedList;
};

class BackgroundImage extends Model {
  static $fields() {
    return {
      imgixParams: this.$attr(),
      src: this.$string(),
      title: this.$string()
    };
  }
}

class Button$1 extends Model {
  static $fields() {
    return _objectSpread2(_objectSpread2({}, super.$fields()), {}, {
      type: this.$string(),
      border: this.$boolean(),
      color: this.$string(),
      textColor: this.$string(),
      label: this.$string(),
      url: this.$string(),
      promotionId: this.$string()
    });
  }
}

class Style extends Model {
  static $fields() {
    return {
      backgroundPosition: this.$string(),
      fontFamily: this.$string(),
      fontSize: this.$string(),
      fontWeight: this.$string(),
      height: this.$string(),
      justifyContent: this.$string(),
      lineHeight: this.$string(),
      marginTop: this.$string(),
      marginBottome: this.$string(),
      textShadow: this.$string(),
      width: this.$string()
    };
  }
}

class Media extends Model {
  static $fields() {
    return {
      desktop: this.$relateOne(Style),
      tablet: this.$relateOne(Style)
    };
  }
}

class ButtonStyle extends Model {
  static $fields() {
    return {
      backgroundColor: this.$string(),
      responsiveStyles: this.$relateOne(Media)
    };
  }
}

class ContentBlockElement extends Model {
  constructor(record) {
    super(record);
  }
  static $fields() {
    return {
      type: this.$string()
    };
  }
}
// This is for Headline and TextArea Element
class ContentBlockElementContent extends ContentBlockElement {
  static $fields() {
    return _objectSpread2(_objectSpread2(_objectSpread2({}, super.$fields()), Style.prototype()), {}, {
      content: this.$string(),
      responsiveStyles: this.$relateOne(Media)
    });
  }
}
// This is for the button group
class ContentBlockElementButtonGroup extends ContentBlockElement {
  static $fields() {
    return _objectSpread2(_objectSpread2({}, super.$fields()), {}, {
      responsiveStyles: this.$relateOne(Media),
      buttons: this.$relateMany(Button)
    });
  }
}
class Button extends ContentBlockElement {
  static $fields() {
    return _objectSpread2(_objectSpread2({}, super.$fields()), {}, {
      label: this.$string(),
      responsiveStyles: this.$relateOne(Media),
      url: this.$string(),
      width: this.$string()
    });
  }
}

class ContainerStyles extends Model {
  static $fields() {
    return _objectSpread2(_objectSpread2({}, Style.prototype), {}, {
      responsiveStyles: this.$relateOne(Media)
    });
  }
}

class ContentBlock extends Model {
  static $fields() {
    return {
      backgroundImage: this.$relateOne(BackgroundImage),
      containerStyles: this.$relateOne(ContainerStyles),
      elements: this.$array(ContentBlockElement),
      elementsContainerStyles: this.$relateOne(ContainerStyles),
      title: this.$string()
    };
  }
}

class ContentSection extends Model {
  static $fields() {
    return {
      layout: this.$string(),
      // hero|secondary|tertiary|hero6
      contentBlocks: this.$relateMany(ContentBlock)
    };
  }
}

class MetaData extends Model {
  static $fields() {
    return {
      description: this.$string(),
      title: this.$string()
    };
  }
}

class ContentPage extends Model {
  static $fields() {
    return {
      allowedCountries: this.$array(),
      market: this.$array(),
      meta: this.$relateOne(MetaData),
      path: this.$string(),
      sections: this.$relateMany(ContentSection),
      title: this.$string()
    };
  }
}

/* istanbul ignore file */
class Imgix extends Model {
  static $fields() {
    return {
      focalPointX: this.$string(),
      focalPointY: this.$string()
    };
  }
}

export { Address, AmerOrderHistory, Attributes, BackgroundImage, BloomreachProductTiles, BreadcrumbList, Button$1 as Button, ButtonStyle, Cart, CartAddress, CartProduct, CartfulProductTile, CatalogFilterFacets, CatalogProduct, CategoryIntro, ContainerStyles, ContentBlock, ContentBlockElement, ContentBlockElementButtonGroup, ContentBlockElementContent, ContentPage, ContentSection, CustomerTip, ForgeRockUser, Image, Imgix, MagentoCart, MagentoCartAddress, MagentoProduct, MagentoStore, Media, MetaData, Model, Product, ProductInventory, ProductPDP, ProductTile, Promo, Store, Style, Variant, adaptFilterFacets as adaptBloomreachFilterFacets, adaptProductTile$1 as adaptBloomreachProductTile, adaptCartfulProductTile, adaptOrderHistoryToCart, adaptProductTile, adaptRefinementsApplied, adaptSort, getPurchaseLimitsGroupTypeValue, getTotalPriceFromOrders };
//# sourceMappingURL=index.js.map
